import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPins, getHotelPins, deletePin, deleteHotelPin, reactivatePin, resumePausedPin, saveDeviceToken, getTierLimits, markOnboardingSeen, getReferralStats, getAchievements, createFreeze, getFreezes, getPinHistory, startTrialWatch, fixStuckRetirements } from '../api/atlast';
import { supabase, getLocalUser } from '../supabase';
import axios from 'axios';
import { theme } from '../theme';
import { PinIcon, TagIcon, BellIcon } from '../Icons';
import AppNav from '../AppNav';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import { requestNotificationPermission, notificationsAlreadyGranted } from '../firebaseClient';
import LimitModal from './LimitModal';
import OnboardingTour from './OnboardingTour';
import PinBundlePurchaseModal from './PinBundlePurchaseModal';
import FreezePaymentModal from './FreezePaymentModal';
import FreezeDurationModal, { FREEZE_DURATION_TIERS } from './FreezeDurationModal';
import ReactivateDateModal from './ReactivateDateModal';
import PurchaseSuccessModal from './PurchaseSuccessModal';
// EngagementStrip replaces MomentumBar's direct render here (Sep 4,
// per Ali's report) — merges the achievement status, streak, and
// referral cards into one collapsible block instead of 3 separate
// full-height ones, so trip cards need far less scrolling to reach.
// MomentumBar.js itself stays intact (its computeCurrentLevel/TierIcon
// exports are reused by EngagementStrip) for a possible future
// standalone use.
import EngagementStrip from './EngagementStrip';
import PageBackground from './PageBackground';
import { pickRibbon, formatCheckedWhen, formatMoney, cityFromAirportName, pinTitle, friendlyDetail, recoverCreatedFreeze } from '../utils/dealExtras';
import { useDestinationPhoto, sizedPhoto } from '../useDestinationPhoto';
import { watchOutcome, OUTCOME_STYLE } from '../utils/fee';
import SwitchFareModal from './SwitchFareModal';
import { getAltFare, canSwitchFare, altTitle } from '../utils/altFare';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

// Sparkline component removed — dashboard cards no longer show a price
// chart at all. A connected line implied a price "trend" that flight
// prices don't really have (availability-driven noise, not a market
// curve), which risked reading as more meaningful than the data actually
// is. The deal screen now uses a dot strip instead (see DealScreen.js),
// and even that stays off the dashboard to keep cards glanceable.

// Client-side approximation of the backend's tiered-checking schedule
// (get_effective_interval in price_checker.py) — not an exact replica of
// every nuance (e.g. the 24h recently-notified cooldown is skipped here
// for simplicity), but close enough to give an honest "roughly when"
// rather than a precise guarantee. Returns a short label like "~45 min"
// or "any moment now".
// Shared with the deal-found toast logic below and renderTrustDetails —
// one definition so both agree on what "a deal" and "lowest yet" mean.
// Real ambiguity bug (Sep 24, per Ali's report): trip-date ranges on
// pin cards showed "Sep 9 – Sep 24" with no year at all, which reads
// fine for one trip but gives no way to tell a June 2026 pin apart
// from a May 2027 one sitting right next to it. Rule: show the year
// on a date only when it ISN'T the current calendar year — keeps the
// common case (most pins are for this year) exactly as compact as
// before, while a future- or past-year date gets an unambiguous
// "Sep 9, 2027". Departure and return are checked independently, so a
// trip that crosses a New Year (Dec 28 – Jan 3) correctly shows the
// year only on the side that needs it.
function formatTravelDate(dateStr, referenceYear) {
  const d = new Date(dateStr + 'T00:00:00');
  const opts = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== referenceYear) opts.year = 'numeric';
  return d.toLocaleDateString('en-US', opts);
}

function formatTravelDateRange(startStr, endStr) {
  if (!startStr) return '';
  const now = new Date().getFullYear();
  const start = formatTravelDate(startStr, now);
  if (!endStr) return start;
  const end = formatTravelDate(endStr, now);
  return `${start} – ${end}`;
}

function pinIsDeal(pin) {
  const { price, available } = comparablePrice(pin);
  return available && price != null && price <= pin.budget;
}

function pinIsLowestYet(pin) {
  const history = pin.price_history || [];
  const prices = history.map(h => h.price);
  return history.length >= 3 && pin.last_price != null && pin.last_price <= Math.min(...prices);
}

// Shared helper: is this pin's price currency actually known to match its
// budget currency? Before last_price_currency existed on a pin (or before
// it's been re-checked since that fix shipped), we genuinely don't know —
// treated as "assume match" rather than "assume mismatch", since flagging
// every pin as mismatched before they've been re-checked would be its own
// kind of false alarm.
function currencyMismatch(pin) {
  if (!pin.last_price_currency) return false;
  const budgetCurrency = pin.currency || 'CAD';
  return pin.last_price_currency !== budgetCurrency;
}

function priceCurrencyLabel(pin) {
  return pin.last_price_currency || pin.currency || 'CAD';
}

// The price to actually use for any budget comparison (diffs, %, sorting,
// bars). When currencies match, that's just last_price/best_value_price
// as always. When they don't match, prefer the server-converted
// last_price_converted (a real FX conversion, not a guess) — only fall
// back to "can't compare at all" when that conversion genuinely wasn't
// available (FX lookup failed, or this pin hasn't been re-checked since
// the conversion feature shipped).
function comparablePrice(pin) {
  // last_price is authoritative now — the backend sets it from
  // whichever fare the pin's flight_preference actually calls for
  // (best-value-regardless-of-stops, or nonstop-only). Reading
  // best_value_price first here would silently ignore that choice for
  // "direct"-preference pins, same bug as freeze_pricing.py had.
  const rawPrice = pin.last_price;
  if (!currencyMismatch(pin)) {
    return { price: rawPrice, isConverted: false, available: rawPrice != null };
  }
  if (pin.last_price_converted != null) {
    return { price: pin.last_price_converted, isConverted: true, available: true };
  }
  return { price: null, isConverted: false, available: false };
}

const RING_RADIUS = 24;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// A stat ring that sweeps in from empty to its value exactly once — the
// very first time `revealed` becomes true — and never replays after
// that, even though the underlying value keeps changing every poll.
// `playedRef` is local to each ring instance: once it flips true it
// stays true for the component's lifetime, so a later poll that updates
// `pct` just jumps the ring straight to the new value instead of
// re-sweeping from empty.
function AnimatedRing({ pct, color, value, revealed, size = 60 }) {
  const playedRef = useRef(false);
  const shouldAnimate = revealed && !playedRef.current;

  useEffect(() => {
    if (revealed) playedRef.current = true;
  }, [revealed]);

  const clamped = Math.max(0, Math.min(1, pct));
  const targetOffset = RING_CIRCUMFERENCE * (1 - clamped);

  return (
    <svg width={size} height={size} viewBox="0 0 60 60">
      <circle cx="30" cy="30" r={RING_RADIUS} fill="none" stroke={theme.border} strokeWidth="6" />
      <circle
        cx="30" cy="30" r={RING_RADIUS} fill="none" stroke={color} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={RING_CIRCUMFERENCE}
        strokeDashoffset={shouldAnimate ? RING_CIRCUMFERENCE : targetOffset}
        transform="rotate(-90 30 30)"
        className={shouldAnimate ? 'ring-sweep' : undefined}
        style={shouldAnimate ? { '--ring-target-offset': targetOffset } : undefined}
      />
      <text x="30" y="35" textAnchor="middle" fontSize="13" fontWeight="700" fill={theme.textPrimary} fontFamily="monospace">
        {value}
      </text>
    </svg>
  );
}

// Same one-shot reveal pattern as AnimatedRing: sweeps in from empty to
// its width exactly once when `revealed` first turns true, then holds —
// later polls that change `pct` just jump the bar to its new width
// instead of re-sweeping. `index` staggers each row's start slightly so
// the reveal reads as a cascade down the list, not everything at once.
function AnimatedBar({ label, pct, index, revealed }) {
  const playedRef = useRef(false);
  const shouldAnimate = revealed && !playedRef.current;

  useEffect(() => {
    if (revealed) playedRef.current = true;
  }, [revealed]);

  const clamped = Math.max(0, Math.min(100, pct));
  const color = clamped <= 75 ? theme.success : theme.warning;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      <div style={{ width: '68px', fontSize: '12px', color: theme.textPrimary, fontWeight: 600, flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1, background: theme.bg, borderRadius: '0px', height: '16px', overflow: 'hidden' }}>
        <div
          className={shouldAnimate ? 'bar-sweep' : undefined}
          style={{
            height: '100%', background: color, borderRadius: '0px',
            width: shouldAnimate ? '0%' : `${clamped}%`,
            ...(shouldAnimate ? { '--bar-target-width': `${clamped}%`, animationDelay: `${index * 0.08}s` } : {})
          }}
        />
      </div>
      <div style={{ width: '36px', textAlign: 'right', fontSize: '12px', fontWeight: 700, color: clamped <= 75 ? theme.success : theme.textPrimary }}>
        {clamped.toFixed(0)}%
      </div>
    </div>
  );
}

// Split-flap "departure board" — each pin gets its own row: a padded
// destination name and a status that's either "READY" (green, this pin
// has crossed its own due threshold) or a live MM:SS countdown to when
// it will. Real per-pin data from getNextCheck/due_at, not a shared
// single guess — several pins can honestly show READY at once, since
// the backend checks them in one batch per tick rather than one at a
// time (see the /trips/next-check route). A "ready" row still waits for
// the next shared tick to actually be checked — this only tells you
// it's eligible, not that it's happening this instant.
function SplitFlapRow({ label, dueAt, isDue }) {
  const [display, setDisplay] = useState(isDue ? 'READY' : '--:--:--');

  useEffect(() => {
    if (isDue || !dueAt) {
      setDisplay(isDue ? 'READY' : '--:--:--');
      return;
    }
    const target = new Date(dueAt).getTime();
    const tick = () => {
      const rem = target - Date.now();
      if (rem <= 0) { setDisplay('READY'); return; }
      // Always HH:MM:SS — unambiguous (no way to tell "4 minutes" from
      // "4 hours" just by looking at "04:05"), and ticks live the whole
      // time, not just when imminent.
      const totalSeconds = Math.floor(rem / 1000);
      const hours = Math.min(99, Math.floor(totalSeconds / 3600));
      const mins = Math.floor((totalSeconds % 3600) / 60);
      const secs = totalSeconds % 60;
      setDisplay(`${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [dueAt, isDue]);

  const ready = display === 'READY';
  const parts = display.split(':');

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap',
      gap: '6px 12px', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.18)'
    }}>
      <div style={{ fontWeight: 700, fontSize: '16px', color: '#FFFFFF', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
        {label}
      </div>
      {ready ? (
        <span className="ready-badge-pulse" style={{
          background: '#FFFFFF', color: '#2B4A3E', fontWeight: 700, fontSize: '12px',
          padding: '5px 12px', borderRadius: '0px'
        }}>
          READY
        </span>
      ) : (
        <div style={{ fontWeight: 700, fontSize: '16px', fontFamily: "'Courier New', monospace", color: '#FFFFFF' }}>
          {parts.map((part, i) => (
            <React.Fragment key={i}>
              {part}
              {i < parts.length - 1 && <span className="colon-blink">:</span>}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
}

function SplitFlapBoard({ pins }) {
  if (!pins || pins.length === 0) return null;

  // Ready pins first, then soonest due_at — same "what's coming up"
  // ordering as everything else on the dashboard.
  const sorted = [...pins].sort((a, b) => {
    if (a.is_due !== b.is_due) return a.is_due ? -1 : 1;
    const at = a.due_at ? new Date(a.due_at).getTime() : Infinity;
    const bt = b.due_at ? new Date(b.due_at).getTime() : Infinity;
    return at - bt;
  });

  return (
    <div style={{ background: '#3D6B5C', borderRadius: '0px', padding: '18px', marginTop: '16px' }}>
      <div style={{
        textAlign: 'center', color: '#FFFFFF', fontWeight: 700, fontSize: '15px',
        textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '6px'
      }}>
        Next Check In
      </div>
      {sorted.map((p, i) => (
        <SplitFlapRow key={i} label={p.label} dueAt={p.due_at} isDue={p.is_due} />
      ))}
    </div>
  );
}

// Replaces the old renderNextCheckBadge() plain-function version — this
// needs its own live per-second ticking for the final-10-seconds
// countdown, which a plain function returning JSX can't do; it has to
// be a real component with its own state.
//
// Four phases: 'waiting' (normal "Next check in N min/hr" state, gold
// circle), 'countdown' (final 10 seconds — bouncing circle, color
// intensifying gold -> orange), 'zero' (the instant it fires — green
// pop, magnifying-glass icon, expanding ring burst), 'settled' (brief
// "All set — watching again" confirmation before reverting to waiting
// once the parent's next real poll of /trips/next-check produces a
// fresh dueAt). The zero/settled sequence is deliberately neutral
// ("watching again", not "deal found!") — this badge has no idea what
// the check actually found; that's the separate deal-toast system's
// job, not this one's to imply.
// Photo-backed card header (Aug 16, per Ali's report — design item 4
// UI wiring, "Option 1" from the earlier mockup review: full photo
// header with a gradient overlay for legibility). A real top-level
// component, not inline JSX inside renderUrgentCard — useDestinationPhoto
// is a hook, and renderUrgentCard/renderRegularCard are plain functions
// called in a .map() loop, not components themselves, so calling a
// hook directly inside them would violate the Rules of Hooks. This
// wraps just the header portion; everything below it (price, budget
// line, actions) is unchanged.
//
// Scoped to urgent cards only for now — regular (non-urgent) cards use
// an animated CSS gradient class for their header background
// (regular-card-header-cycle), not a plain color, and layering a photo
// on top of that needs more care than this pass covers. Falls back to
// the exact same theme.structural flat color whenever no photo is
// available (Pexels not configured, no results, or still loading) —
// nothing about the existing look changes until a real photo actually
// exists for that destination.
function PinCardPhotoHeader({ destination, children }) {
  const photoUrl = useDestinationPhoto(destination);

  if (!photoUrl) {
    return (
      <div style={{ background: theme.structural, padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
        {children}
      </div>
    );
  }

  return (
    <div style={{
      backgroundImage: `linear-gradient(180deg, rgba(31,50,69,0.15) 0%, rgba(31,50,69,0.85) 100%), url(${sizedPhoto(photoUrl, 640, 200)})`,
      backgroundSize: 'cover', backgroundPosition: 'center',
      padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px'
    }}>
      {children}
    </div>
  );
}

// NextCheckBadge removed entirely (Aug 17, per Ali's report — the
// three-tier reorganization). It rendered the live countdown circle
// that used to overlay urgent-card photos ("next check in 1hr") —
// already dropped from the urgent tier specifically during the card
// redesign (Option 1: "next check timing isn't information anyone
// acts on"), and generalizing that same card treatment to all three
// tiers here removed its last remaining caller (the old
// renderRegularCard). getNextCheckMatch (below, in Dashboard()) is
// removed for the same reason — it existed only to feed this
// component's props.


function Dashboard() {
  const navigate = useNavigate();
  // Scroll-to-pin on load (Sep 16, per Ali's report — the other half of
  // making a deal-alert push notification actually useful once clicked:
  // it lands here via /dashboard?pin=<id> (see notifications.py's
  // send_deal_notification for why the link points at the Dashboard
  // with a query param rather than straight at the deal page). null
  // once consumed so this only ever fires once per visit, not on every
  // re-render.
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightPinId, setHighlightPinId] = useState(searchParams.get('pin'));
  const [pins, setPins] = useState([]);

  // Scroll to and briefly highlight the pin a notification click was
  // actually about (Sep 16, per Ali's report), once pins have actually
  // loaded — scrolling before then would have nothing to scroll to.
  // Clears the ?pin= param right after so refreshing the page doesn't
  // keep re-triggering the scroll/highlight on every reload.
  useEffect(() => {
    if (!highlightPinId || pins.length === 0) return;
    const el = document.getElementById(`pin-card-${highlightPinId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('pin');
      return next;
    }, { replace: true });
    const timer = setTimeout(() => setHighlightPinId(null), 3000);
    return () => clearTimeout(timer);
  }, [pins, highlightPinId, setSearchParams]);
  const [loading, setLoading] = useState(true);
  // Account menu dropdown (Sep 7, per Ali's report — Phase 1 of the
  // account-settings menu). Replaces the standalone Sign Out link;
  // Payments and Trip History deliberately stay as their own visible
  // nav links, not folded in here — they're checked often enough that
  // burying them a click deeper adds real friction, unlike the
  // genuinely settings-like items this menu actually holds.
  // First name (Sep 8, per Ali's report) — purely for the "Welcome
  // back, {name}" greeting below. Stays null for any account that
  // never set one (every pre-existing account, or anyone who skipped
  // it at signup) — the greeting itself handles that fallback, this
  // just holds whatever's actually on the row, including null.
  const [firstName, setFirstName] = useState(null);
  const [accountEmail, setAccountEmail] = useState(null);
  // Bonus Pin chip dismissal (Sep 8, per Ali's report — real bug,
  // confirmed live: clicked the chip, pinned a trip, came back, chip
  // still showed as if nothing happened). Confirmed the backend side
  // first — bonus_pin_credits genuinely is a permanent addition to
  // max_active_pins (routes/trips.py), not a one-time token that gets
  // consumed, so there's nothing to "use up" there; the fix belongs
  // entirely on the frontend, hiding a nudge once it's been acted on.
  // Same Preferences-based, device-local dismissal pattern as the
  // "Get instant alerts" banner's own dismissNotifPrompt — not a
  // backend flag, since this is a one-time "seen it" acknowledgment,
  // not a change to the underlying (permanent, correct) bonus itself.
  //
  // Stores the credit COUNT at dismissal time, not a plain true/false —
  // bonus_pin_credits is cumulative and can grow later (another
  // referral, another streak milestone), and a flat boolean would
  // permanently hide the chip even after a genuinely new bonus arrived.
  // Comparing current count against the dismissed count lets a real
  // new credit reactivate the chip instead of being silently suppressed
  // by an old dismissal.
  const BONUS_PIN_DISMISSED_COUNT_KEY = 'flyatlast_bonus_pin_dismissed_at_count';
  const [, setBonusPinDismissedAtCount] = useState(null);
  useEffect(() => {
    Preferences.get({ key: BONUS_PIN_DISMISSED_COUNT_KEY }).then(({ value }) => {
      if (value != null) setBonusPinDismissedAtCount(Number(value));
    });
  }, []);
  // Bonus Pin chip dismissal, take 2 (Sep 14, per Ali's report — real
  // bug: clicking the chip dismissed it immediately, even if the user
  // backed out of /pin without actually creating a trip, so a genuinely
  // unused bonus slot could vanish from view having never been acted
  // on). The chip should only go away once the click actually resulted
  // in a new pin — not on the click itself. Records the active-pin
  // count at the moment of the click; dismissal itself is deferred to
  // the effect below, which only fires it once that count has gone up.
  const BONUS_PIN_PENDING_CLICK_COUNT_KEY = 'flyatlast_bonus_pin_pending_click_active_pins';
  const [bonusPinPendingClickCount, setBonusPinPendingClickCount] = useState(null);
  useEffect(() => {
    Preferences.get({ key: BONUS_PIN_PENDING_CLICK_COUNT_KEY }).then(({ value }) => {
      if (value != null) setBonusPinPendingClickCount(Number(value));
    });
  }, []);
  useEffect(() => {
    getLocalUser().then(async ({ data }) => {
      if (!data?.user?.id) return;
      if (data.user.email) setAccountEmail(data.user.email);
      const { data: userRow } = await supabase
        .from('users')
        .select('first_name')
        .eq('id', data.user.id)
        .single();
      if (userRow?.first_name) setFirstName(userRow.first_name);
    });
  }, []);
  // expandedPinId removed (Aug 16, per Ali's report) — its only
  // consumer was the Details toggle, itself removed for showing
  // nothing that wasn't already visible elsewhere on the card.
  const [notifStatus, setNotifStatus] = useState('unset'); // 'unset' | 'enabling' | 'enabled' | 'denied'
  const [tierLimits, setTierLimits] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  // Pin-bundle purchase (Aug 31, per Ali's report — Phase A of the
  // pin-purchase build). tierLimits was already fetched here before
  // this change but never actually rendered anywhere — the "Trips
  // watching" stat chip below now shows it and becomes a real, clickable
  // purchase entry point once the limit is hit, rather than a user
  // discovering the cap only by having pin creation itself fail.
  const [showPinPurchaseModal, setShowPinPurchaseModal] = useState(false);
  // Pin-slot attribution breakdown popover — removed (Sep 8, per Ali's
  // report). This click-toggled popover state is no longer needed:
  // the breakdown it controlled is now always shown inline as part of
  // the rebuilt status breakdown below, not hidden behind a click.
  const [pinPurchaseSuccess, setPinPurchaseSuccess] = useState(false);
  const [referralStats, setReferralStats] = useState(null);
  const [pinDetailsOpen, setPinDetailsOpen] = useState(false);
  const [showAllPaused, setShowAllPaused] = useState(false);
  // Dashboard's own copy-link feedback for the new persistent referral
  // card below (Aug 20, per Ali's report) — separate from
  // ReferralPrompt's own `copied` state, since that popup may not even
  // be mounted/visible when this card's button is clicked.
  const [dashboardReferralCopied, setDashboardReferralCopied] = useState(false);
  // Extracted to component scope (Sep 4, per Ali's report) so
  // EngagementStrip can receive it as a plain prop, now that the
  // referral card itself lives inside that shared component instead
  // of its own inline IIFE. Native share where available (mobile —
  // opens the real share sheet: WhatsApp, Messages, Mail, whatever's
  // installed), falling back to clipboard copy on desktop or wherever
  // the Web Share API isn't supported. One tap to an actual send, not
  // copy-then-hunt-for-the-app.
  const handleDashboardShare = async (e) => {
    e.stopPropagation();
    if (!referralStats?.referral_code) return;
    const link = `https://app.flyatlast.com/?ref=${referralStats.referral_code}`;
    const shareData = {
      title: 'FlyAtlast',
      text: "Send this to whoever you always text flight deals to anyway — you both get a bonus pin slot the moment they join.",
      url: link,
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch {
        // User cancelled the share sheet, or share failed — fall
        // through to clipboard so the action still does something
        // rather than silently no-op'ing.
      }
    }
    try {
      await navigator.clipboard.writeText(link);
      setDashboardReferralCopied(true);
      setTimeout(() => setDashboardReferralCopied(false), 2000);
    } catch {
      // Clipboard API can fail on non-HTTPS or without permission —
      // same best-effort handling as ReferralPrompt's own handleCopy.
    }
  };
  // Deal Alerts prompt is now dismissible/snoozable (Aug 20, per Ali's
  // report) rather than a permanent fixture — same 3-day snooze pattern
  // ReferralPrompt.js already uses via Capacitor Preferences, so a user
  // who consciously wants email-only isn't nagged every single visit.
  const NOTIF_PROMPT_SNOOZE_KEY = 'flyatlast_notif_prompt_snoozed_until';
  const [notifPromptSnoozed, setNotifPromptSnoozed] = useState(false);
  const [achievements, setAchievements] = useState(null);
  // Mirrors userIdRef as real state (refs don't trigger re-renders) —
  // needed so FreezeTracker can be handed a stable userId prop once
  // auth resolves, rather than reaching into a ref from outside.
  const [currentUserId, setCurrentUserId] = useState(null);
  // Tracks the "Freeze this price" button on the Dashboard itself
  // (separate entry point from the Deal page's FreezePitchCard) —
  // freezingPinId disables the clicked card's button while the request
  // is in flight, freezeErrorByPin holds a per-pin inline message so
  // one pin's failed attempt doesn't affect any other pin's button.
  const [freezingPinId, setFreezingPinId] = useState(null);
  const [freezeErrorByPin, setFreezeErrorByPin] = useState({});
  // Global trial entry point (Sep 24, per Ali's report, approved
  // mockup) — replaces the per-card "Try free for 24h" offer with one
  // account-level button that opens a picker over current deal-ready
  // pins, or an empty-state prompt if there are none yet.
  const [showTrialPicker, setShowTrialPicker] = useState(false);
  // Real bug found live (Sep 2, per Ali's report): this Dashboard
  // freeze entry point called createFreeze directly with no
  // confirmation step at all — a genuinely separate code path from
  // DealScreen.js's, which already went through FreezePaymentModal
  // correctly. Tracks which pin's modal should be showing (or null),
  // rather than a plain boolean, since the modal needs that pin's own
  // fee/currency context.
  const [freezeModalPin, setFreezeModalPin] = useState(null);
  // Duration picker step (Sep 8, per Ali's report — real bug, caught
  // via screenshot comparison against DealScreen.js: this Dashboard
  // entry point still skipped straight to the payment modal with a
  // hardcoded 5-day/$9.99 assumption, while DealScreen.js's own entry
  // point already asked first. freezeDurationModalPin holds which
  // pin's duration is currently being chosen (or null) — same
  // single-flow-at-a-time model as freezeModalPin itself, since only
  // one freeze flow is ever in progress across the whole Dashboard at
  // once. selectedDurationHours isn't per-pin for the same reason —
  // there's only ever one active selection to track.
  const [freezeDurationModalPin, setFreezeDurationModalPin] = useState(null);
  const [selectedDurationHours, setSelectedDurationHours] = useState(120);
  // Carried forward from the duration step into the payment step, same
  // reasoning as DealScreen.js's matching state (Sep 23).
  const [selectedQuote, setSelectedQuote] = useState(null);
  // Keyed by pin_id → active freeze, so renderFreezeBanner can look up
  // a given pin's freeze in O(1) rather than re-scanning the full list
  // per card render. Only active freezes are kept — a resolved one has
  // nothing to show on this compact banner (FreezeDetailScreen is
  // still the place to see a resolved freeze's outcome).
  const [activeFreezesByPin, setActiveFreezesByPin] = useState({});
  // Real gap found live (Sep 4, per Ali's report): once a freeze
  // resolves, the pin fell straight back to a plain "Watching" card
  // with zero indication a freeze ever happened or what it resolved
  // to — that history only lived in Purchase History. Tracks the
  // most recent resolved freeze per pin (kept or refunded) so the
  // card itself can say so, persisting indefinitely per Ali's
  // decision rather than fading after a few days.
  const [resolvedFreezesByPin, setResolvedFreezesByPin] = useState({});
  // Oct 19: pin currently being offered "track flights with stops instead".
  const [switchFarePin, setSwitchFarePin] = useState(null);
  // Bridges the exact gap confirmed on Aug 26: a freeze that resolves to
  // 'kept' should let the person submit proof of a higher real purchase
  // price (FreezeDetailScreen already has this whole flow built and
  // working), but until now nothing on this Dashboard ever linked to a
  // RESOLVED freeze — renderFreezeBanner below only fires for freezes
  // still in activeFreezesByPin, and a freeze is removed from that map
  // the instant it resolves (see the .then() below). Once resolved,
  // the pin's card reverted to a plain "eligible for a new freeze"
  // state with zero trace of the one that just finished — confirmed
  // live (Ali's LHR-LAX freeze, Aug 26): resolved to 'kept' correctly
  // on the backend, resolution email sent correctly, but no way at all
  // to reach the upload screen from the Dashboard or the pin's Deal
  // page. This tracks 'kept' freezes still inside their real 48-hour
  // evidence window (matches FREEZE_EVIDENCE_WINDOW_HOURS server-side)
  // so a genuine banner+link can render for exactly that case.
  const EVIDENCE_WINDOW_HOURS = 48;
  const [evidenceEligibleFreezesByPin, setEvidenceEligibleFreezesByPin] = useState({});

  // Rotation index for the insight banner — declared here near the
  // other top-level state, actual rotation effect lives further down
  // once insightItems (derived from pins) exists.
  const [insightIndex, setInsightIndex] = useState(0);
  // Flips true once, right after the first successful load, and never
  // resets — this is what tells the stat rings and comparison bars to
  // play their one-time sweep-in reveal. Subsequent polls update the
  // underlying numbers without ever re-triggering the animation (see
  // AnimatedRing/AnimatedBar's own local playedRef for how that's
  // enforced per-element).
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    if (!loading) setRevealed(true);
  }, [loading]);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
  // Suppressed entirely inside a real native build (Aug 23, per Ali's
  // report, added for the Capacitor mobile app) — "add to your home
  // screen" is a nonsensical prompt for someone who already has the
  // real, installed native app open. Capacitor.isNativePlatform() is
  // false for the website and for the app opened in a regular mobile
  // browser, so this nudge still shows exactly where it always did —
  // this only turns it off for the one context it never made sense in.
  const showIOSInstallNudge = isIOS && !isStandalone && notifStatus !== 'enabled' && !Capacitor.isNativePlatform();
  const userIdRef = useRef(null);
  // Holds the previous fetch's pins so each new fetch can be compared
  // against it — null on the very first load, deliberately, so a pin
  // that was already a deal before the page even opened never toasts
  // (only a genuine over-to-under-budget transition counts).
  const prevPinsRef = useRef(null);
  const [toastQueue, setToastQueue] = useState([]);
  const [activeToast, setActiveToast] = useState(null);
  // Shown instead of a plain alert() when a free-tier user hits the
  // urgent-marking limit (0 allowed) — a dead-end alert with no next
  // step isn't useful to someone who might actually want to upgrade.
  // null when hidden; a string (the backend's detail message) when shown.
  const [upgradePrompt, setUpgradePrompt] = useState(null);
  const [isPinLimitFromReactivate, setIsPinLimitFromReactivate] = useState(false);

  const fetchPins = async (userId) => {
    try {
      const [tripData, hotelData] = await Promise.all([
        getPins(userId),
        getHotelPins(userId).catch(() => ({ pins: [] }))
      ]);
      const tripPins = (tripData.pins || []).map(p => ({ ...p, pin_type: 'flight' }));
      const hotelPins = (hotelData.pins || []).map(p => ({ ...p, pin_type: 'hotel' }));
      const newPins = [...tripPins, ...hotelPins];

      // Deal-found toast — only ever fires on a real transition detected
      // between this fetch and the previous one, never on a timer and
      // never repeated for a pin that was already a deal. "Lowest price
      // yet" merges into this same toast rather than firing separately,
      // so a deal that also happens to be the cheapest seen doesn't
      // produce two toasts for one event.
      if (prevPinsRef.current) {
        const newlyFoundDeals = newPins.filter(pin => {
          const prevPin = prevPinsRef.current.find(p => p.id === pin.id);
          return prevPin && !pinIsDeal(prevPin) && pinIsDeal(pin);
        });
        if (newlyFoundDeals.length > 0) {
          setToastQueue(q => [
            ...q,
            ...newlyFoundDeals.map(pin => {
              const { price, isConverted, available } = comparablePrice(pin);
              return {
                key: `${pin.id}-${pin.last_checked}`,
                label: pinTitle(pin),
                price,
                budget: pin.budget,
                // The RAW price's currency (e.g. USD) — only meaningful
                // for the "converted from X" sub-label, never for the
                // price/budget figures themselves.
                rawCurrency: priceCurrencyLabel(pin),
                // The currency `price` (above) is actually denominated
                // in: when isConverted, comparablePrice() already
                // returned a converted figure in the BUDGET's currency,
                // not the raw price's — pairing it with rawCurrency
                // (e.g. showing a converted PKR number labeled "USD")
                // was the exact bug this field exists to prevent.
                currency: pin.currency || 'CAD',
                isConverted,
                mismatch: !available,
                isLowestYet: pinIsLowestYet(pin)
              };
            })
          ]);
        }
      }
      prevPinsRef.current = newPins;
      setPins(newPins);
    } catch (error) {
      console.error('Error fetching pins:', error);
    }
  };

  // Shows one toast at a time from the queue — never stacks. Each stays
  // up for 6s, then the next queued one (if any) takes its place.
  useEffect(() => {
    if (!activeToast && toastQueue.length > 0) {
      const [next, ...rest] = toastQueue;
      setActiveToast(next);
      setToastQueue(rest);
      const timer = setTimeout(() => setActiveToast(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [toastQueue, activeToast]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stored = await Preferences.get({ key: NOTIF_PROMPT_SNOOZE_KEY });
        const snoozedUntil = parseInt(stored.value || '0', 10);
        if (!cancelled && Date.now() < snoozedUntil) setNotifPromptSnoozed(true);
      } catch {
        // Preferences can still throw in unusual environments — same
        // best-effort handling as ReferralPrompt's own snooze check;
        // treat as "not snoozed" rather than breaking init.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismissNotifPrompt = async () => {
    try {
      await Preferences.set({ key: NOTIF_PROMPT_SNOOZE_KEY, value: String(Date.now() + 3 * 24 * 60 * 60 * 1000) });
    } catch {
      // Failed write just means it may reappear sooner than intended —
      // not worth surfacing an error over, same reasoning as
      // ReferralPrompt's own dismiss().
    }
    setNotifPromptSnoozed(true);
  };

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await getLocalUser();
      userIdRef.current = user.id;
      setCurrentUserId(user.id);
      await fetchPins(user.id);
      // Tier limits fetched once, separately from the poll loop — this
      // doesn't change on its own the way pin data does, so no need to
      // re-fetch it every poll cycle.
      getTierLimits(user.id).then((limits) => {
        setTierLimits(limits);
        // Onboarding tour (Sep 14, per Ali's report) — has_seen_onboarding
        // piggybacked onto this same response rather than a separate
        // fetch, since this call already runs once on every mount.
        if (limits?.has_seen_onboarding === false) {
          setShowOnboarding(true);
        }
      }).catch(() => null);
      getReferralStats(user.id).then(setReferralStats).catch(() => null);
      getAchievements(user.id).then(setAchievements).catch(() => null);
      getFreezes(user.id).then(res => {
        const byPin = {};
        const evidenceByPin = {};
        const resolvedByPin = {};
        const nowMs = Date.now();
        (res.freezes || []).forEach(f => {
          if (f.status === 'active') byPin[f.pin_id] = f;
          // 'kept' means the fee was charged because the tracked price
          // held — that's exactly the case where someone who actually
          // paid more in real life has something to submit. A freeze
          // missing resolved_at entirely (shouldn't happen once actually
          // resolved, but defends against exactly the kind of stuck-job
          // state this whole check exists because of) is treated as
          // outside the window rather than erroring or showing forever.
          // Also excludes any freeze with an evidence_status already set
          // (Sep 5, per Ali's report — real gap caught live from a
          // screenshot: a freeze whose evidence was already submitted,
          // reviewed, AND refunded was still showing "Submit proof — 24h
          // left" on the Dashboard for the entire 48-hour window, because
          // this check had never once looked at whether evidence existed
          // — only at the freeze's own status/resolved_at, neither of
          // which changes when evidence is submitted or resolved.
          // evidence_status now comes from the backend's /freezes
          // response (a real join against freeze_evidence, added at the
          // same time as this fix) — pending_review, approved, AND
          // declined all correctly suppress this prompt, since in every
          // one of those cases something has already been submitted and
          // there's nothing left for the person to do here.
          // !f.is_trial added (Sep 24, per Ali's report — the
          // unconditional-retirement rewrite): a trial's own 'kept'
          // outcome now also sets lifetime_cap_reached=True at
          // retirement, same as a real charge, so without this check
          // a trial that "would have been charged" would incorrectly
          // qualify for Submit Proof — there's nothing to submit
          // proof against when nothing was ever actually charged.
          if (f.status === 'kept' && !f.is_trial && f.resolved_at && !f.evidence_status) {
            const hoursSinceResolved = (nowMs - new Date(f.resolved_at).getTime()) / 3600000;
            if (hoursSinceResolved >= 0 && hoursSinceResolved < EVIDENCE_WINDOW_HOURS) {
              evidenceByPin[f.pin_id] = f;
            }
          }
          // Most recent resolved outcome (kept or refunded) per pin —
          // a pin frozen more than once over its lifetime should show
          // its latest outcome, not an older one that's since been
          // superseded by a newer freeze attempt.
          if ((f.status === 'kept' || f.status === 'refunded') && f.resolved_at) {
            const existing = resolvedByPin[f.pin_id];
            if (!existing || new Date(f.resolved_at) > new Date(existing.resolved_at)) {
              resolvedByPin[f.pin_id] = f;
            }
          }
        });
        setActiveFreezesByPin(byPin);
        setEvidenceEligibleFreezesByPin(evidenceByPin);
        setResolvedFreezesByPin(resolvedByPin);
      }).catch(() => null);
      setLoading(false);
    };
    init();

    const POLL_INTERVAL_MS = 5000;
    const POLL_CUTOFF_MS = 90000;
    const startedAt = Date.now();

    const pollInterval = setInterval(async () => {
      if (!userIdRef.current || Date.now() - startedAt > POLL_CUTOFF_MS) {
        clearInterval(pollInterval);
        return;
      }
      // Speed fix (Oct 6): this used to refetch ALL pins (24 kB, heavy on the
      // server) and achievements every 5s for 90s on every Dashboard visit,
      // even when nothing was waiting on a first check. Now it only polls
      // while a pin is still waiting for its first result, stops as soon as
      // none is, and pauses while the tab is in the background.
      if (document.hidden) return;
      const known = prevPinsRef.current;
      if (known && !known.some(p => p.is_active !== false && !p.last_checked)) {
        clearInterval(pollInterval);
        return;
      }
      await fetchPins(userIdRef.current).catch(() => null);
      // Real bug found live (Aug 26/30): getAchievements was only ever
      // fetched once, on initial mount — unlike pins, it never refreshed
      // during the same 90s polling window pins already use. So earning
      // a badge while the Dashboard was open never updated the nav-bar
      // level chip until a full page reload. Reusing the exact same
      // poll tick/cutoff pins already use, rather than inventing a
      // separate cadence — achievements don't need 5s granularity, but
      // matching pins' own window means "did something that could earn
      // a badge, then looked at the chip a few seconds later" now just
      // works, without adding a second independent polling mechanism.
      getAchievements(userIdRef.current).then(setAchievements).catch(() => null);
    }, POLL_INTERVAL_MS);

    return () => clearInterval(pollInterval);
  }, []);

  // nextCheckInfo removed entirely (Aug 17, per Ali's report — a
  // follow-up flagged, not chased down, in the three-tier build: this
  // state and its 60s polling effect only ever fed NextCheckBadge/
  // getNextCheckMatch, both removed earlier in that same pass when the
  // countdown circle was dropped from every card tier. Left running
  // for a bit as a known, real inefficiency (an unnecessary recurring
  // API call with zero consumers) rather than something silently
  // ignored — now actually removed.

  // Sign-out now sends people to the marketing site rather than straight
  // back to this app's own login screen — flyatlast.com is a separate
  // domain/repo, so this needs a real browser redirect (navigate() can't
  // cross domains, it would just re-hit our own '/' route).
  const handleSignOut = async () => {
    await supabase.auth.signOut();
    window.location.href = 'https://flyatlast.com';
  };

  const getFreshnessState = (timestamp) => {
    if (!timestamp) return { label: 'Checking...', color: theme.textMuted, warn: false, live: false };
    const minutes = Math.floor((new Date() - new Date(timestamp)) / 60000);
    const timeStr = new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    if (minutes < 15) {
      // "live" only applies here — this is the one state where the pulse
      // dot is honest. Aging/stale prices get a plain static dot instead,
      // so the animation itself never implies freshness that isn't real.
      return { label: `Checked at ${timeStr}`, color: theme.freshnessOk, warn: false, live: true };
    } else if (minutes < 30) {
      return { label: `Checked at ${timeStr}`, color: theme.freshnessAging, warn: false, live: false };
    } else {
      return { label: `Checked at ${timeStr} — verify before booking`, color: theme.danger, warn: true, live: false };
    }
  };

  // Small live indicator next to the freshness label. Only pulses when
  // freshness.live is true (checked under 15 min ago) — a static dot
  // otherwise, so the animation never implies the price is fresher than
  // it actually is.
  const FreshnessDot = ({ freshness }) => (
    <span style={{ position: 'relative', display: 'inline-block', width: '9px', height: '9px', marginRight: '5px', verticalAlign: 'middle' }}>
      {freshness.live && <span className="pulse-ring" style={{ borderColor: freshness.color }} />}
      <span style={{ position: 'absolute', inset: '2px', background: freshness.color, borderRadius: '50%' }} />
    </span>
  );

  // toggleUrgent removed (Aug 31, per Ali's report — Phase 4 of the new
  // pin model). Mark Urgent no longer exists as a general pin attribute
  // anywhere in the UI — the backend's /urgent endpoint is left in
  // place (harmless, unreachable dead code) rather than deleted, same
  // treatment as trips.py's own toggle_urgent/max_urgent_pins from
  // Phase 1, in case a later pass wants to formally remove that side
  // too.

  const respondToUrgentPrompt = async (pin, stillInterested) => {
    try {
      const base = pin.pin_type === 'hotel' ? '/hotels' : '/trips';
      await axios.post(`${API_BASE}${base}/${pin.id}/urgent-response`, {
        still_interested: stillInterested
      });
      await fetchPins(userIdRef.current);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Something went wrong. Try again.');
      console.error('Error responding to urgent prompt:', error);
    }
  };

  // respondToCheckCountPrompt removed (Sep 5, per Ali's report —
  // confirmed dead code, not just superseded). It called a "still
  // interested?" prompt endpoint keyed on check_count_response_pending
  // and CHECK_COUNT_PAUSE_THRESHOLD (99 checks) — both leftover from
  // the old tiered pin model. A repo-wide backend search confirmed
  // check_count_response_pending is never actually set to True
  // anywhere post-rewrite (the flat 10-check lifetime cap deactivates
  // a pin long before check 99 could ever be reached), so this banner
  // and handler were unreachable, not just redundant. The backend
  // endpoint now returns 410 Gone rather than being deleted outright.

  const dismissBudgetPrompt = async (pin) => {
    try {
      const base = pin.pin_type === 'hotel' ? '/hotels' : '/trips';
      await axios.post(`${API_BASE}${base}/${pin.id}/budget-prompt-response`, {
        action: 'dismiss'
      });
      await fetchPins(userIdRef.current);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Something went wrong. Try again.');
      console.error('Error dismissing budget prompt:', error);
    }
  };

  const updateBudget = async (pin, currentBudget) => {
    const newBudget = window.prompt('Update your budget (CAD):', currentBudget);
    if (!newBudget) return;
    const parsed = parseFloat(newBudget);
    if (isNaN(parsed) || parsed <= 0) {
      alert('Please enter a valid budget.');
      return;
    }
    try {
      const base = pin.pin_type === 'hotel' ? '/hotels' : '/trips';
      await axios.patch(`${API_BASE}${base}/${pin.id}/budget`, { budget: parsed });
      await fetchPins(userIdRef.current);
    } catch (error) {
      alert(error?.response?.data?.detail || 'Something went wrong. Try again.');
      console.error('Error updating budget:', error);
    }
  };

  const [confirmDeletePin, setConfirmDeletePin] = useState(null);

  const handleDeletePin = (pin) => {
    // Now opens the same styled confirm shell as every limit modal in
    // this app (Sep 15, per Ali's report — real formatting complaint:
    // this used a raw browser confirm() dialog, the generic
    // "app.flyatlast.com says" box, instead of anything actually
    // themed). "This can't be undone" was accurate before Phase A
    // (Aug 23, per Ali's report) — it no longer is. Deleting now just
    // pauses live tracking; the pin stays visible with a Reactivate
    // option for 30 days (or indefinitely if reactivated), same as
    // auto-pause from hitting a check-count threshold. Only a pin
    // that's fully exhausted its lifetime check cap is genuinely,
    // permanently done.
    setConfirmDeletePin(pin);
  };

  const executeDeletePin = async () => {
    const pin = confirmDeletePin;
    setConfirmDeletePin(null);
    if (!pin) return;
    try {
      if (pin.pin_type === 'hotel') {
        await deleteHotelPin(pin.id);
      } else {
        await deletePin(pin.id);
      }
      await fetchPins(userIdRef.current);
    } catch (error) {
      setUpgradePrompt(error?.response?.data?.detail || 'Something went wrong. Try again.');
      console.error('Error deleting pin:', error);
    }
  };

  // Reactivate (Aug 23, per Ali's report) — the Dashboard-side handler
  // for the new /trips/{id}/reactivate endpoint. Deliberately no
  // window.confirm here, unlike delete — reactivating is the safe,
  // reversible-by-deleting-again direction, not the one that needs a
  // speed bump. Surfaces the backend's real refusal message (a pin that
  // hit its lifetime cap) rather than a generic failure string, since
  // that specific message is the whole point of the check.
  // Resume a pin paused for inactivity (Sep 23) — separate from
  // handleReactivatePin below on purpose: that one is for a DEPARTED
  // pin and always needs a new date; this one needs nothing further,
  // same trip, same remaining checks, same price history.
  const handleResumePin = async (pin) => {
    try {
      await resumePausedPin(pin.id);
      await fetchPins(userIdRef.current);
    } catch (error) {
      const rawMessage = error?.response?.data?.detail || 'Something went wrong. Try again.';
      const pinLimitHit = rawMessage.startsWith('PIN_LIMIT_REACHED:');
      setIsPinLimitFromReactivate(pinLimitHit);
      setUpgradePrompt(pinLimitHit ? rawMessage.replace('PIN_LIMIT_REACHED:', '').trim() : rawMessage);
      console.error('Error resuming pin:', error);
    }
  };

  const handleReactivatePin = async (pin) => {
    try {
      await reactivatePin(pin.id);
      await fetchPins(userIdRef.current);
    } catch (error) {
      // Now uses the same styled LimitModal every other limit error in
      // this app uses (Sep 15, per Ali's report — real gap, confirmed
      // live: this was still a raw browser alert(), the one place that
      // hadn't been switched over despite the comment here previously
      // claiming otherwise). upgradePrompt was already wired up and
      // rendered below, just never actually triggered from anywhere.
      // isPinLimitFromReactivate mirrors PinTrip.js's own
      // isPinLimitError/ctaLabel pattern — reactivating is one more
      // real way to hit the cap, so it gets the same "Buy 3 more"
      // shortcut right in the modal, not just a dismiss button.
      const rawMessage = error?.response?.data?.detail || 'Something went wrong. Try again.';
      const pinLimitHit = rawMessage.startsWith('PIN_LIMIT_REACHED:');
      setIsPinLimitFromReactivate(pinLimitHit);
      setUpgradePrompt(pinLimitHit ? rawMessage.replace('PIN_LIMIT_REACHED:', '').trim() : rawMessage);
      console.error('Error reactivating pin:', error);
    }
  };

  // New (Sep 20) — the date-required reactivation path, for
  // deactivation_reason === 'departure_passed' pins only (see
  // reactivate_pin's own docstring in routes/trips.py). Opens
  // ReactivateDateModal instead of calling handleReactivatePin
  // directly; the modal's own submit calls this with the dates once
  // entered. Kept as a fully separate state/handler pair from
  // handleReactivatePin above rather than folding date-handling into
  // it, since lifetime_limit/deleted reactivation still needs none of
  // this and should stay exactly as simple as it already was.
  const [reactivateDatePin, setReactivateDatePin] = useState(null);
  const [reactivateDateSubmitting, setReactivateDateSubmitting] = useState(false);
  const [reactivateDateError, setReactivateDateError] = useState(null);

  const handleReactivateWithDate = async (dates) => {
    const pin = reactivateDatePin;
    if (!pin) return;
    setReactivateDateSubmitting(true);
    setReactivateDateError(null);
    try {
      await reactivatePin(pin.id, dates);
      setReactivateDatePin(null);
      await fetchPins(userIdRef.current);
    } catch (error) {
      // Same PIN_LIMIT_REACHED handling as handleReactivatePin above,
      // but surfaced inline in this modal rather than via the shared
      // LimitModal/upgradePrompt — the person is already mid-flow in a
      // form here, so closing this modal to open a different one for
      // the error would lose the date they just entered.
      const rawMessage = error?.response?.data?.detail || 'Something went wrong. Try again.';
      const pinLimitHit = rawMessage.startsWith('PIN_LIMIT_REACHED:');
      setReactivateDateError(pinLimitHit ? rawMessage.replace('PIN_LIMIT_REACHED:', '').trim() : rawMessage);
      console.error('Error reactivating pin with new date:', error);
    }
    setReactivateDateSubmitting(false);
  };

  // "Freeze this price" from the Dashboard card itself — mirrors
  // DealScreen.js's flow now (Sep 2, per Ali's report — real bug fix):
  // opens the same confirmation modal instead of calling createFreeze
  // directly with no confirmation step at all. Stays on the Dashboard
  // while the modal is open; on success, hands off to the same
  // FreezeDetailScreen every other freeze entry point uses.
  const handleFreezeFromDashboard = (pin) => {
    setFreezeErrorByPin(prev => ({ ...prev, [pin.id]: null }));
    setFreezeDurationModalPin(pin);
  };

  // Free trial, started directly from the Dashboard card (Sep 24) —
  // same "skip the duration/payment steps entirely" reasoning as
  // DealScreen.js's handleStartTrial. Reuses freezingPinId/
  // freezeErrorByPin (the same per-card busy/error state the paid
  // button already uses) rather than adding a parallel set — the two
  // buttons are mutually exclusive per pin card (trialAvailable
  // switches which one renders), so there's no real risk of them
  // fighting over the same state.
  const handleStartTrialFromDashboard = async (pin) => {
    setFreezeErrorByPin(prev => ({ ...prev, [pin.id]: null }));
    setFreezingPinId(pin.id);
    try {
      const result = await startTrialWatch(pin.id, currentUserId);
      setFreezingPinId(null);
      navigate('/freeze', { state: { freezeId: result.freeze.id, userId: currentUserId, justCreated: true, pin } });
    } catch (error) {
      setFreezingPinId(null);
      setFreezeErrorByPin(prev => ({ ...prev, [pin.id]: friendlyDetail(error?.response?.data?.detail) || 'Could not start the free trial. Try again.' }));
      if (error?.response?.status === 400 && error?.response?.data?.detail) { alert(friendlyDetail(error.response.data.detail)); fetchPins(currentUserId)?.catch?.(() => {}); }
    }
  };

  // Duration chosen (Sep 8, per Ali's report) — the hand-off between
  // the two steps: closes the duration picker, records the choice,
  // then opens the real payment modal for the pin that was pending.
  // Mirrors handleDurationChosen in DealScreen.js exactly.
  const handleDurationChosenFromDashboard = (hours, quote) => {
    const pin = freezeDurationModalPin;
    setSelectedDurationHours(hours);
    setSelectedQuote(quote);
    setFreezeDurationModalPin(null);
    setFreezeModalPin(pin);
  };

  // Same explanatory-confirmation pattern as DealScreen.js's own
  // handleFreeze (Sep 3, per Ali's report) — navigation held until
  // "Got it" is clicked, not fired immediately alongside the modal.
  const [pendingFreezeNav, setPendingFreezeNav] = useState(null);

  const handleConfirmFreezeFromDashboard = async (paymentMethodId = null, saveCard = false) => {
    console.log('[DIAG] Dashboard handleConfirmFreezeFromDashboard called');
    const pin = freezeModalPin;
    setFreezeModalPin(null);
    setFreezingPinId(pin.id);
    const startedAt = Date.now();
    try {
      const result = await createFreeze(pin.id, currentUserId, paymentMethodId, saveCard, selectedDurationHours);
      console.log('[DIAG] Dashboard createFreeze succeeded, result:', result);
      setPendingFreezeNav({ freezeId: result.freeze.id, userId: currentUserId, justCreated: true });
      console.log('[DIAG] Dashboard setPendingFreezeNav called');
    } catch (error) {
      // If this ever falls back to the generic message again, this log
      // is what tells us why — whether the backend genuinely sent no
      // detail, or the request failed at the network/CORS level before
      // a response ever came back (error.response would be undefined
      // in that case, which is indistinguishable from "no detail" in
      // the UI alone).
      console.error('Freeze request failed:', error);
      // No answer from the server at all (dropped/slow connection): the watch
      // may have been created anyway, so check before showing an error.
      if (!error?.response) {
        const made = await recoverCreatedFreeze(pin.id, currentUserId, startedAt);
        if (made) {
          setPendingFreezeNav({ freezeId: made.id, userId: currentUserId, justCreated: true });
          return;
        }
      }
      setFreezeErrorByPin(prev => ({
        ...prev,
        [pin.id]: friendlyDetail(error?.response?.data?.detail) || 'We couldn\'t confirm your Price Watch. Check your connection, then look for it on your Dashboard before trying again.'
      }));
      if (error?.response?.status === 400 && error?.response?.data?.detail) { alert(friendlyDetail(error.response.data.detail)); fetchPins(currentUserId)?.catch?.(() => {}); }
    } finally {
      setFreezingPinId(null);
    }
  };

  const handleEditPin = (pin) => {
    navigate('/pin', { state: { prefill: pin } });
  };

  const handleEnableNotifications = async () => {
    setNotifStatus('enabling');
    const token = await requestNotificationPermission();
    if (!token) {
      setNotifStatus('denied');
      return;
    }
    try {
      await saveDeviceToken(userIdRef.current, token);
      setNotifStatus('enabled');
    } catch (error) {
      console.error('Error saving device token:', error);
      setNotifStatus('denied');
    }
  };

  // Already allowed on this device: hide the prompt and quietly refresh
  // the saved token (tokens can rotate). Never shows a permission dialog.
  useEffect(() => {
    if (!currentUserId) return undefined;
    let cancelled = false;
    (async () => {
      if (!(await notificationsAlreadyGranted()) || cancelled) return;
      setNotifStatus('enabled');
      try {
        const token = await requestNotificationPermission();
        if (token && !cancelled) await saveDeviceToken(currentUserId, token);
      } catch (error) {
        console.warn('Quiet push token refresh failed:', error);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUserId]);

  // Hybrid grouping (Aug 31, per Ali's report — Phase 4 of the new pin
  // model, replacing the old 4-tier Urgent/Frozen/Quiet/Paused split
  // entirely now that Urgent no longer exists as a pin attribute).
  // Design decision, made explicitly rather than defaulted to: with
  // every free user capped at 3 pins total, 3-4 separate section
  // headers were organizational overhead with nothing left to
  // organize. One highlighted "Deal Ready" section for the one state
  // actually worth calling out, everything else (Frozen/Watching/
  // Paused) as a single flat list below, sorted by real priority
  // instead of separate headers — Frozen first (already protected,
  // worth the reassurance), then Watching, then Paused last but never
  // hidden (it's the exact moment the new deactivation banner exists
  // to catch). Each card in the flat list carries its own status badge
  // (see renderPinCard's stubLabel) instead of a section header doing
  // that job.
  // Real count of pins that actually count against max_active_pins
  // (Sep 5, per Ali's report — real bug caught live from a screenshot
  // showing "4 / 3" on the Trips-watching stat chip). pins.length was
  // being used for that stat and for the buy-more-pins card below it,
  // but pins includes every pin this Dashboard shows — including
  // paused/deactivated ones still sitting here with a Reactivate
  // button — not just the ones the backend's real limit actually
  // counts (routes/trips.py's active_count query, always correctly
  // filtered to is_active=True). So this was a display-only bug, never
  // a real capacity leak, but a confusing one: a limit that looked
  // broken when it wasn't.
  const activePinsCount = pins.filter(p => p.is_active !== false).length;

  // Completes the bonus-pin dismissal fix started above: once a pending
  // click exists and the real active-pin count has since risen above
  // it, that's confirmation a pin actually got created — now it's safe
  // to dismiss the chip and clear the pending marker. If the count
  // hasn't moved (e.g. the user backed out of /pin without pinning
  // anything), this simply does nothing and the chip keeps showing.
  useEffect(() => {
    if (bonusPinPendingClickCount == null || !referralStats) return;
    if (activePinsCount > bonusPinPendingClickCount) {
      setBonusPinDismissedAtCount(referralStats.bonus_pin_credits);
      Preferences.set({ key: BONUS_PIN_DISMISSED_COUNT_KEY, value: String(referralStats.bonus_pin_credits) }).catch(() => null);
      setBonusPinPendingClickCount(null);
      Preferences.remove({ key: BONUS_PIN_PENDING_CLICK_COUNT_KEY }).catch(() => null);
    }
  }, [activePinsCount, bonusPinPendingClickCount, referralStats]);

  // !evidenceEligibleFreezesByPin[p.id] added (Sep 24, per Ali's
  // report — real bug, fixed at the source: a pin with a real 'kept'
  // charge genuinely stays is_active=true through its 48h Submit-
  // Proof window now (see freeze_resolver.py's retire_pins_past_
  // evidence_window), so without this it would show up here too —
  // Deal Ready badge, working "Start a Price Watch" button — at the
  // same time its Submit Proof card is showing below. A pin in that
  // state should show ONLY the Submit Proof card, nothing else.
  const dealReadyPins = pins.filter(p => pinIsDeal(p) && !activeFreezesByPin[p.id] && !evidenceEligibleFreezesByPin[p.id] && p.is_active !== false);
  const frozenPins = pins.filter(p => activeFreezesByPin[p.id]);
  // evidenceEligibleFreezesByPin excluded here too (Sep 29, per Ali's
  // report — closing out Finding 21 for real, not just on paper). This
  // exclusion already existed on dealReadyPins just above (see that
  // line's own comment), but was never carried down to this one — the
  // Tracking section built from watchingPins stayed clean in practice
  // only because a proof-window pin almost always still has a real,
  // available comparablePrice() (checking stopped, but the last real
  // price is still on the pin), which happens to fail restPins' own
  // "!comparablePrice(p).available" filter further down. That's a
  // coincidence, not a guarantee — if a proof-window pin's last known
  // price were ever unavailable (a currency conversion failure, stale
  // no_flights data from before the watch started), it would leak into
  // Tracking and reproduce the exact duplicate-card problem this
  // exclusion is meant to prevent. Explicit now, not incidental.
  const watchingPins = pins.filter(p => !pinIsDeal(p) && !activeFreezesByPin[p.id] && !evidenceEligibleFreezesByPin[p.id] && p.is_active !== false);
  const pausedTierPins = pins.filter(p => p.is_active === false);
  // Lifetime-capped-and-permanently-done pins move to Trip-History-only
  // now, with one deliberate exception (Sep 8, per Ali's report — the
  // refinement we agreed on before building this): a lifetime-capped
  // pin whose freeze just resolved almost always crosses the cap in
  // the same moment (a 5-day freeze runs up to 60 checks, ~6x the
  // 10-check cap) — moving it off the Dashboard immediately would take
  // the live "Submit proof" 48-hour countdown with it, right when
  // someone's most likely checking their Dashboard for exactly that.
  // So: still shown here, but only while that window is genuinely open.
  // Once it closes — submitted, or the deadline passes — it drops out
  // of evidenceEligibleFreezesByPin on its own and this pin quietly
  // stops qualifying, moving to Trip-History-only with no separate
  // cleanup step needed.
  // Rebuilt to source from ALL pins, not pausedTierPins (Sep 24, per
  // Ali's report — the corrected design: a pin with a real 'kept'
  // charge stays genuinely is_active=true through its 48h Submit-
  // Proof window now, so it was never in pausedTierPins (is_active
  // === false) at all under the earlier "retire immediately" version
  // this reverts. This is the real fix, not a workaround — the
  // Submit-Proof card's actual gate is simply "does this pin have a
  // currently-open evidence-eligible freeze," independent of whatever
  // the pin's own active/paused state happens to be.
  const evidenceWindowPausedPins = pins.filter(p => evidenceEligibleFreezesByPin[p.id]);
  // Excludes evidenceWindowPausedPins entirely, not just
  // lifetime_cap_reached pins broadly — those get their own dedicated,
  // headed section below instead (renderTierSection('Submit proof', ...)),
  // so they must NOT also appear again in the unsectioned flat list,
  // or every evidence-window pin would render as two separate cards.
  //
  // dashboardVisiblePausedPins now becomes its own headed "Paused"
  // section below (Sep 8, per Ali's report — real point raised
  // directly: someone might specifically be looking to reactivate one
  // of these, which is a genuine available action, same category as
  // Deal Ready/Submit Proof/Needs Attention — not the "nothing to do
  // but wait" state Watching and Frozen actually are). No longer feeds
  // restPins at all; it's rendered as its own section further down.
  //
  // Narrowed to exactly "checks remaining, limit not reached" (Sep 15,
  // then reconsidered Sep 18, both per Ali's report). The Sep 15
  // version excluded departure_passed entirely, on the reasoning that
  // reactivating a pin whose date already passed isn't a real action.
  // Reconsidered after seeing it live: it IS a real action once you
  // account for reactivate-then-edit-to-a-new-date as one coherent
  // flow — reactivating just resumes tracking (no check fires from
  // that alone; only edit_pin ever triggers one, when a real new date
  // is actually picked), and edit_pin now correctly blocks editing an
  // inactive pin with a clear message (Sep 18 fix) rather than the
  // confusing "date must be future" error this used to produce. So
  // the real dividing line was never the reason — it's whether checks
  // remain at all. lifetime_cap_reached (by any path, including a
  // freeze cycle whose evidence window has closed) still means no
  // checks left, no real action, Trip History only — that part is
  // unchanged. Legacy pins from before deactivation_reason existed
  // (reason is null) are still shown here rather than hidden, since
  // there's no way to tell after the fact which of those were a
  // delete vs. a limit.
  const dashboardVisiblePausedPins = pausedTierPins.filter(p => !p.lifetime_cap_reached);

  // Dashboard status breakdown (Sep 8, per Ali's report — real gap:
  // the 3 old stat tiles didn't visibly reconcile into a full picture,
  // and two real, already-tracked pin states (no results ever found,
  // 3+ consecutive check failures) weren't shown anywhere at all).
  // Split out of watchingPins rather than double-counted — every pin
  // lands in exactly one of these buckets, so the counts genuinely
  // sum to activePinsCount + pausedTierPins.length, which is the
  // actual "math" that was missing before. "Not tracking" is
  // deliberately NOT further split into paused/limit-reached/deleted —
  // Ali's explicit call: deletion uses the same is_active=false flag
  // as automatic pausing, so those three reasons aren't reliably
  // distinguishable in the data today, and he doesn't want that
  // distinction built right now regardless.
  const watchingNoResultsPins = watchingPins.filter(p => p.last_check_status === 'no_flights' || p.last_check_status === 'no_availability');
  const watchingWithDataPins = watchingPins.filter(p => p.last_check_status !== 'no_flights' && p.last_check_status !== 'no_availability' && !(p.last_check_status === 'error' && (p.consecutive_failures || 0) >= 3));
  // Revisit Budget (Sep 15, per Ali's report — real point: a pin
  // priced even $1 over budget was sitting in the same flat Watching
  // list as one with no price data at all, no way to tell "not a deal
  // yet, might become one" apart from "not a deal yet, and here's
  // exactly what it'd take." No percentage threshold — the old "So
  // Close" 25% cutoff doesn't actually matter for whether something
  // deserves a decision; the raised-budget amount itself already
  // communicates how big an ask it is, so anything with a real,
  // comparable price above budget belongs here regardless of by how
  // much. Carved out of watchingWithDataPins specifically (not
  // watchingPins broadly) so a pin still searching with zero
  // comparable price yet — nothing to revisit a budget against — stays
  // in plain Watching below instead.
  const revisitBudgetPins = watchingWithDataPins.filter(p => comparablePrice(p).available);
  // Same evidenceEligibleFreezesByPin exclusion as watchingPins above,
  // and for the same reason (Sep 29, per Ali's report — Finding 21):
  // this is built directly from `pins`, not from watchingPins, so it
  // didn't inherit that fix — a proof-window pin whose last_check_status
  // happened to be a real error before the watch started (checking
  // stops once a watch begins, so this status is frozen at whatever it
  // was then) could otherwise show in Needs Attention at the same time
  // as its own Submit Proof card.
  const needsAttentionPins = pins.filter(p => p.is_active !== false && !activeFreezesByPin[p.id] && !evidenceEligibleFreezesByPin[p.id] && p.last_check_status === 'error' && (p.consecutive_failures || 0) >= 3);

  // Only Watching now — the one genuinely open-ended state with no
  // defined endpoint (Sep 8, per Ali's report — real readability
  // problem, confirmed directly: with zero visual boundary between
  // "Paused" and this flat list, a Frozen card sitting immediately
  // below the Paused header read as if it belonged to Paused, purely
  // from spatial adjacency, even though the underlying data was always
  // correct). Frozen gets its own header below instead — unlike
  // Watching, it has a real, live countdown (Day X of 5) and a defined
  // resolution, closer in kind to the other time-bound sections
  // (Submit Proof) than to open-ended Watching. needsAttentionPins and
  // revisitBudgetPins both stay excluded — subsets of watchingPins by
  // definition, each already broken out into its own headed section.
  const restPins = [...watchingWithDataPins.filter(p => !comparablePrice(p).available), ...watchingNoResultsPins];

  // Split of pausedTierPins by WHERE a pin actually lives, not why it
  // stopped (Sep 8, per Ali's report — real bug, confirmed live: the
  // Dashboard's "archived" count included every is_active=false pin,
  // but Trip History's own real rule (routes/trips.py) only shows a
  // pin once it's lifetime_cap_reached OR deactivated 30+ days ago —
  // a pin paused more recently than that is deliberately still meant
  // to be visible right on the Dashboard's own pin list, Reactivate
  // button and all, not yet moved to history. The old "archived" link
  // promised all 5 would be on that page; only 1 actually was).
  // Mirrors the backend's exact condition so this is a true partition
  // of pausedTierPins — every pin lands in exactly one bucket, so
  // archivedPins.length + stillOnDashboardPins.length always equals
  // pausedTierPins.length, and the whole reconciliation (active +
  // archived + still-on-dashboard = every pin ever created) holds by
  // construction, not by coincidence.
  // Corrected (Sep 8, per Ali's report — real bug, confirmed live: 16
  // vs 50+ actual entries on Trip History). The previous version of
  // this tried to compute the true archived total from pausedTierPins
  // — but pausedTierPins comes from Dashboard's own `pins` state,
  // which get_pins (routes/trips.py) DELIBERATELY only populates with
  // active pins plus pins deactivated in the last 30 days, for good
  // reason (keeps the Dashboard from accumulating years of long-dead
  // pins). Anything deactivated before that window is never fetched
  // into `pins` at all — not filtered out client-side, genuinely never
  // received. No client-side condition can recover data the frontend
  // was never given, which is exactly why the last fix's logic was
  // correct but its result was still wrong.
  //
  // The only place with true, complete visibility is the same backend
  // query Trip History's own endpoint already runs — so archivedCount
  // below is now fetched for real via that same endpoint
  // (getPinHistory), not derived from local pins.
  //
  // stillOnDashboardPins is exactly dashboardVisiblePausedPins (defined
  // above alongside evidenceWindowPausedPins) — reused here rather than
  // redeclared, since it's the same set for the same reason: every pin
  // meeting this condition (recently deactivated, not lifetime-capped)
  // is guaranteed to already be present in `pins`. archivedCount +
  // stillOnDashboardPins.length now sums to every genuinely inactive
  // pin exactly once — lifetime-capped pins (including the ones still
  // visible here during their evidence window) count only inside
  // archivedCount, never twice.
  const stillOnDashboardPins = dashboardVisiblePausedPins;
  const [archivedCount, setArchivedCount] = useState(null);
  useEffect(() => {
    if (!currentUserId) return;
    getPinHistory(currentUserId)
      .then(result => setArchivedCount((result.pins || []).length))
      .catch(() => setArchivedCount(null));
  }, [currentUserId]);

  // Moved out of renderStatsRow — now used by the intro paragraph
  // ("918 checks run across 7 active trips") instead of its own stat
  // box, since a single sentence up top does the same job without
  // adding another card to the page.
  const totalChecks = pins.reduce((sum, p) => sum + (p.check_count || 0), 0);

  // Rotating insight banner (Aug 16/17, per Ali's report — replacing
  // the old static "We're watching prices for you, 24/7" masthead).
  // Built from real, currently-actionable pins only — a pin already
  // frozen has nothing new to surface here, since its own gold banner
  // already covers it. Uses pickRibbon's own honest badges (Rare find,
  // New low, Down X%) for the headline when one applies, rather than
  // a generic "is a deal" line, so the banner says the SAME specific
  // thing the pin's own card ribbon does.
  //
  // is_active filter added (Sep 14, per Ali's report — real bug: this
  // never checked is_active, unlike dealReadyPins below, which does.
  // A paused pin's last recorded price can still satisfy pinIsDeal
  // even though nothing has re-checked it since — the banner was
  // capable of surfacing a deal for a pin that had no corresponding
  // "Deal Ready" card to actually click through to, or that dropped
  // straight into "Paused" underneath it. Same condition dealReadyPins
  // uses (Sep 29, per Ali's report — Finding 21: this had drifted out
  // of sync with dealReadyPins, which already added the
  // evidenceEligibleFreezesByPin exclusion; this one hadn't, so a
  // proof-window pin whose last known price still happened to be at or
  // under budget could rotate through this banner promoting it as a
  // live deal, while its own Submit Proof card sat right below saying
  // the watch had already resolved).
  const insightItems = pins.filter(p => pinIsDeal(p) && !activeFreezesByPin[p.id] && !evidenceEligibleFreezesByPin[p.id] && p.is_active !== false).map(p => {
    const label = pinTitle(p);
    const ribbon = pickRibbon(p);
    const { price: comparable, available: hasComparable } = comparablePrice(p);
    const priceLabel = hasComparable && comparable != null ? `${formatMoney(comparable)} ${p.currency || 'CAD'}` : null;
    return {
      key: p.id,
      label,
      ribbonLabel: ribbon ? ribbon.label : null,
      fallbackHeadline: `${label} is a deal`,
      sub: priceLabel ? `from ${priceLabel}` : null,
      onClick: () => navigate(p.pin_type === 'hotel' ? '/hotel-deal' : '/deal', { state: { pin: p } }),
    };
  });

  const activeFreezeCount = Object.keys(activeFreezesByPin).length;

  // Rotates every 10s, standard carousel timing — long enough to read,
  // short enough not to feel stale. Skips entirely when there's 0 or 1
  // items: rotating through a single item would look like a bug, not
  // a feature, and there's nothing to show at all when nothing's
  // currently actionable.
  useEffect(() => {
    if (insightItems.length <= 1) {
      setInsightIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setInsightIndex(i => (i + 1) % insightItems.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [insightItems.length]);

  // Top-3 deal ranking (medals) — removed entirely (Sep 15, per Ali's
  // report — real point: with pin counts now varying a lot across
  // testing, this would need constant re-tuning as more pins get added
  // or removed, for a ranking that was really just decoration on top
  // of what the ribbons already communicate). No replacement — pin
  // cards no longer carry any top-right rank badge at all.

  // "avg deal" and "deals now" boxes, and the standalone "premium pins"
  // card that used to live here, are gone — the former is now a ribbon
  // on each trip card (pickRibbon in utils/dealExtras.js), "checks run"
  // moved into the intro paragraph, and "premium pins" is now a small
  // inline pill next to it, rather than a whole card for one number.

  // Trip comparison, rebuilt as sorted horizontal bars — every trip
  // normalized to % of its own budget (current price, not history), so a
  // $300 trip and a $2,800 trip compare fairly. Replaces the old
  // overlapping-line chart, which became unreadable once two or more
  // trips landed at similar percentages (the lines sat almost on top of
  // each other and required cross-referencing a separate color legend).
  // Sorting by percentage means the best and worst deals are obvious at
  // a glance, with the label right on the row instead of in a legend.
  // Achievement badges — every one of these is computed server-side from
  // real pin data (see growth.py's _compute_achievements), never a
  // client-side guess. Earned badges shown first as colored pills, then
  // up to two in-progress ones with a real progress bar (not decorative
  // — it's actual_value/target from the backend). Restyled onto a navy
  // gradient card with rotating pill colors so this reads as a lively
  // celebration rather than a flat checklist.
  const ACHIEVEMENT_PILL_COLORS = [
    { bg: '#3D6B8C', text: '#FFFFFF' },
    { bg: '#6BC190', text: '#0B2E1F' },
    { bg: '#2D7A8C', text: '#FFFFFF' },
    { bg: '#D0A6E8', text: '#3D1F52' },
    { bg: '#7FB8E0', text: '#0F3A5C' },
    { bg: '#8A6FB0', text: '#FFFFFF' },
  ];

  const renderAchievements = () => {
    if (!achievements?.achievements?.length) return null;
    const earned = achievements.achievements.filter(a => a.earned);
    const inProgress = achievements.achievements.filter(a => !a.earned).slice(0, 2);
    const allEarned = achievements.earned_count === achievements.total_count;

    return (
      <div style={{
        background: theme.structural,
        borderRadius: '0px', padding: '20px 22px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '6px' }}>
          <div style={{ fontFamily: theme.fontDisplay, fontSize: '16px', fontWeight: 700, color: theme.cream }}>
            {allEarned ? 'All ' + achievements.total_count + ' badges unlocked' : 'Achievements'}
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(253,246,236,0.75)' }}>
            {achievements.earned_count} of {achievements.total_count} earned
          </div>
        </div>

        {earned.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: inProgress.length > 0 ? '16px' : '0' }}>
            {earned.map((a, i) => {
              const c = ACHIEVEMENT_PILL_COLORS[i % ACHIEVEMENT_PILL_COLORS.length];
              return (
                <span
                  key={a.key}
                  title={a.description}
                  style={{
                    background: c.bg, color: c.text,
                    borderRadius: '0px', padding: '7px 13px', fontSize: '12px', fontWeight: 700
                  }}
                >
                  {a.label}
                </span>
              );
            })}
          </div>
        )}

        {inProgress.map(a => (
          <div key={a.key} style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12.5px', marginBottom: '3px' }}>
              <span style={{ color: theme.cream, fontWeight: 600 }}>{a.label}</span>
              <span style={{ color: 'rgba(253,246,236,0.7)' }}>{a.progress}/{a.target}</span>
            </div>
            <div style={{ background: 'rgba(253,246,236,0.2)', borderRadius: '0px', height: '6px', overflow: 'hidden' }}>
              <div style={{
                height: '100%', borderRadius: '0px', background: theme.cream,
                width: `${Math.min(100, (a.progress / a.target) * 100)}%`
              }} />
            </div>
          </div>
        ))}
      </div>
    );
  };


  const renderPromptBanners = (pin) => {
    const label = pinTitle(pin);
    return (
      <>
        {/* Departure-approaching reminder (Sep 16, per Ali's report —
            the departure-timing rework: this fires at 24h before
            departure, well before handle_pin_expiry actually stops
            tracking at 12h — see that function's own docstring in
            price_checker.py for the full reasoning on both numbers).
            Deliberately the first banner in this list — of everything
            renderPromptBanners can show, a pin whose window to act is
            closing is the most time-sensitive one, ahead of budget or
            urgent-response prompts that have no hard deadline attached. */}
        {/* Suppressed on a pin with an active freeze (Sep 19, per
            Ali's report — real point, caught live from a screenshot:
            this message ("book now or we'll stop tracking") describes
            handle_pin_expiry's own behavior for a regular pin — a
            currently-frozen pin's fate near departure is handled
            entirely differently, by resolve_freezes_nearing_departure
            resolving the freeze itself using the price already locked
            in, not by simply stopping tracking. Telling someone to
            "book soon" about a price freeze that's already protecting
            them, using wording for a mechanism that doesn't even apply
            to their pin, was actively confusing rather than helpful. */}
        {pin.departure_reminder && !activeFreezesByPin[pin.id] && (
          <div style={{
            background: '#FBEBD9',
            border: `1px solid ${theme.urgentBadge}`,
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '12px',
            fontSize: '13px',
            // maxWidth/overflowWrap added (Sep 18, per Ali's report —
            // real bug, caught live from a screenshot: this card was
            // visibly wider than its sibling in the same grid row.
            // pin-card-grid's columns are equal (1fr each) by design,
            // but a CSS grid track still grows to fit content it can't
            // shrink below — so unwrappable text here could force its
            // whole column wider than the other one. Shortened the
            // copy itself below, which was the main driver, and added
            // this as a defensive floor so it can't recur from some
            // future longer string.
            maxWidth: '100%',
            overflowWrap: 'anywhere',
            boxSizing: 'border-box'
          }}>
            <div style={{ color: theme.textPrimary }}>
              {/* Shortened (Sep 18, per Ali's report — real point: this
                  read as too much text for what it needed to say, and
                  was the direct cause of the width mismatch above). */}
              Book soon — tracking stops
              {pin.departure_stop_tracking_at ? ` ${formatCheckedWhen(pin.departure_stop_tracking_at)}` : ` in ${pin.departure_stop_tracking_hours || 12}h`}
            </div>
          </div>
        )}

        {/* "Why is this paused" explanation (Sep 19, per Ali's report
            — real point raised directly, from a live screenshot: a
            departure_passed pin showed "PAUSED" and a Reactivate
            button, but nothing actually explained why — a deleted pin
            is self-explanatory (the user did that themselves), but a
            departure_passed one isn't obvious at a glance. Matches the
            same explanatory-banner pattern already used for the
            budget suggestion and the departure reminder above, just
            informational rather than urgent (muted, not the amber
            urgency color) since there's nothing time-sensitive left to
            act on here — reactivating just needs a new date whenever
            the user gets to it. */}
        {/* !activeFreezesByPin guard added (Sep 19, per Ali's report)
            — defensive: the backend fix to handle_pin_expiry means a
            pin should never end up both departure_passed AND actively
            frozen going forward, but this costs nothing to also check
            here directly, for any pin still carrying that
            contradictory state from before the fix deployed.
            !lifetime_cap_reached guard added (Sep 22, per Ali's
            report — real, confirmed live: a Vienna pin whose freeze
            resolved 'kept' still showed this exact banner alongside
            its correct Submit Proof card — telling someone to
            "reactivate and pick new dates" on a pin that's actually
            permanently retired makes no sense once a freeze has
            resolved. renderEditDeleteLinks below already correctly
            swaps its own Reactivate button for "Lifetime check limit
            reached" on this same condition; this banner just never
            got the matching guard. */}
        {pin.deactivation_reason === 'departure_passed' && !activeFreezesByPin[pin.id] && !pin.lifetime_cap_reached && (
          <div style={{
            background: theme.bg,
            border: `1px solid ${theme.border}`,
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '12px',
            fontSize: '13px',
            color: theme.textMuted
          }}>
            This trip's departure date already passed, so we stopped tracking it. Reactivate and pick new dates to start again.
          </div>
        )}

        {/* Inactivity-pause banner (Sep 23) — distinct copy from the
            departure_passed one above: this pin's dates are still
            perfectly valid, we just haven't seen the user in a while.
            No "pick new dates" language, since none is needed. */}
        {pin.pause_reason === 'inactivity' && (
          <div style={{
            background: theme.bg,
            border: `1px solid ${theme.border}`,
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '12px',
            fontSize: '13px',
            color: theme.textMuted
          }}>
            Paused — we hadn't heard from you in a while. Your price history and remaining checks are still here.
          </div>
        )}

        {/* New state (Sep 19, per Ali's report — real point, caught
            live: after reactivating a departure_passed pin, the card
            showed both the now-cleared "reactivate" message above
            AND, separately, a stale "Book soon, tracking stops
            [already-past time]" banner — neither made sense for a
            pin that's active again but still pointing at the same
            past flight it was deactivated for in the first place.
            This is that exact state: active, but with nothing left to
            check until a real, future date is actually picked. No
            checks run in this state either — handle_check_count_
            lifecycle and the regular Duffel-search path both need a
            real reason to run, and a past departure_date was never
            one; editing to a new date is what actually resumes
            anything, same as it already does for a normal edit. */}
        {pin.departure_needs_new_date && (
          <div style={{
            background: theme.bg,
            border: `1px solid ${theme.border}`,
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '12px',
            fontSize: '13px',
            color: theme.textMuted
          }}>
            Set a new departure date to start tracking again — nothing will be checked until you do.
          </div>
        )}

        {pin.urgent_response_pending && (
          <div style={{
            background: '#FBEBD9',
            border: `1px solid ${theme.urgentBadge}`,
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '12px',
            fontSize: '13px'
          }}>
            <div style={{ marginBottom: '8px', color: theme.textPrimary }}>
              Still deciding on {label}?
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => respondToUrgentPrompt(pin, true)}
                style={{ fontSize: '12px', padding: '6px 12px', background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 700 }}
              >
                Yes, keep watching closely
              </button>
              <button
                onClick={() => respondToUrgentPrompt(pin, false)}
                style={{ fontSize: '12px', padding: '6px 12px', background: theme.inactiveGray, color: theme.textMuted, border: 'none', borderRadius: '12px', cursor: 'pointer' }}
              >
                No, I've moved on
              </button>
            </div>
            <div style={{ fontSize: '12px', color: theme.textMuted, marginTop: '6px' }}>
              If we don't hear from you in 2 days, we'll check this less frequently.
            </div>
          </div>
        )}

        {pin.budget_realistic === false && (
          <div style={{
            background: theme.bg,
            border: `1px solid ${theme.border}`,
            borderRadius: '12px',
            padding: '12px',
            marginBottom: '12px',
            fontSize: '13px'
          }}>
            <div style={{ marginBottom: '8px', color: theme.textPrimary }}>
              We haven't found {label} under {pin.budget} {pin.currency || 'CAD'}.
              {pin.avg_price && ` We're seeing an average of ${Math.round(pin.avg_price)} ${pin.currency || 'CAD'}.`}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => updateBudget(pin, pin.budget)}
                style={{ fontSize: '12px', padding: '6px 12px', background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 700 }}
              >
                Update budget
              </button>
              <button
                onClick={() => dismissBudgetPrompt(pin)}
                style={{ fontSize: '12px', padding: '6px 12px', background: theme.inactiveGray, color: theme.textMuted, border: 'none', borderRadius: '12px', cursor: 'pointer' }}
              >
                Keep watching anyway
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  // renderPriceBlock removed (Aug 16, per Ali's report) — its only
  // caller was the Details expand panel, itself removed for showing
  // nothing that wasn't already visible elsewhere on the card. The one
  // genuinely non-redundant thing it did (the currency-mismatch
  // warning) is now inline directly in renderUrgentCard/renderRegularCard
  // instead of buried behind a tap.

  // getNextCheckMatch removed (Aug 17, per Ali's report) — its only
  // consumer was NextCheckBadge, also removed. See that removal's own
  // comment for why.

  // "Anticipation & trust" details — all built from real data already on
  // the pin, nothing manufactured. Deliberately skipped in compact mode
  // (urgent-card grid) where space is tight; shown on regular cards where
  // there's room for it. "Lowest price yet" requires at least 3 history
  // points so it's not trivially true on someone's first or second check.
  const renderTrustDetails = (pin) => {
    if (!pin.last_price) return null;
    const isLowestYet = pinIsLowestYet(pin);
    const createdLabel = pin.created_at
      ? new Date(pin.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : null;

    return (
      <div style={{ marginTop: '10px' }}>
        {isLowestYet && (
          <span style={{
            display: 'inline-block', background: 'rgba(74,155,110,0.12)', color: theme.success,
            fontSize: '11.5px', fontWeight: 700, padding: '2px 7px', borderRadius: '999px', width: 'fit-content', marginBottom: '4px'
          }}>
            Lowest price yet
          </span>
        )}
        {(pin.check_count || 0) > 0 && createdLabel && (
          <div style={{ fontSize: '12px', color: theme.textMuted }}>
            Checked {pin.check_count} time{pin.check_count !== 1 ? 's' : ''} since {createdLabel}
          </div>
        )}
      </div>
    );
  };

  // renderPriceTags removed (Aug 16, per Ali's report) — its only
  // caller was the Details expand panel, itself removed for showing
  // nothing that wasn't already visible elsewhere on the card.

  const renderActionsAndLinks = (pin, compact = false) => (
    <>
      {/* Primary action row — one clean full-width button. Rewritten
          (Aug 31, per Ali's report — Phase 4 of the new pin model):
          the old "View Deals" / "Mark Urgent" toggle collapses to just
          "View Deals" now that Urgent no longer exists as a general pin
          attribute at all. A pin that isn't a deal yet has nothing
          actionable to show here — the card's own price/budget
          delta already communicates "not there yet," so this row
          simply doesn't render rather than showing a dead button.

          Suppressed once a resolved freeze exists for this pin (Sep
          14, per Ali's report — real gap: this deal already got
          frozen and resolved, so "View Deals" was sending someone
          into a live DealScreen for something that's already history,
          right alongside the resolved-freeze badge below (see
          resolvedFreezesByPin) that already links to the actual
          outcome. Now that badge is the one path back into this deal
          once it's resolved, rather than two competing entry points
          telling two different stories. "Freeze this price"
          underneath is unaffected — that's a fresh purchase, not a
          way back into old data, and already re-enables correctly on
          its own via activeFreezesByPin.

          is_active check added to both this and Freeze below (Sep 15,
          per Ali's report — real bug, confirmed live with a screenshot
          and an actual attempted purchase: a paused or lifetime-capped
          pin's last known price can still satisfy pinIsDeal, and
          neither button here ever checked is_active at all — this
          function is entirely separate from renderEditDeleteLinks,
          which is the one that actually branches on is_active for
          Edit/Delete vs. Reactivate. Ali paid for a freeze on exactly
          this kind of pin and nothing happened — no freeze was
          created, the pin didn't reactivate, the payment just went
          nowhere useful. A paused pin should only ever show its last
          known price, read-only, never a live purchase path.

          departure_needs_new_date check added (Sep 20, per Ali's
          report — the Glasgow bug, traced through two screenshots and
          the actual reactivate_pin code together): is_active alone
          isn't enough — a reactivated pin is active again but can
          still be departure_needs_new_date=true (pointing at the same
          past flight it was deactivated for), and pinIsDeal() doesn't
          know that, since it only checks stale cached price vs.
          budget. That's exactly how a freeze got bought on dead flight
          data. reactivate_pin itself now requires a real future date
          before it'll even reactivate a departure_passed pin, so this
          state should mostly stop occurring — this guard is the
          matching defensive floor here, same as create_freeze's own
          server-side check in routes/freezes.py, in case anything
          else ever leaves a pin in this combination.) */}
      {pin.is_active !== false && !pin.departure_needs_new_date && pinIsDeal(pin) && !resolvedFreezesByPin[pin.id] && (
        <div style={{ display: 'flex', gap: compact ? '5px' : '8px', marginTop: compact ? '10px' : '12px', alignItems: 'center' }}>
          <button
            onClick={() => navigate(pin.pin_type === 'hotel' ? '/hotel-deal' : '/deal', { state: { pin } })}
            style={{
              flex: 1, background: '#fff', color: theme.structural, border: `2px solid ${theme.structural}`,
              borderRadius: '12px', padding: compact ? '7px 4px' : '10px',
              fontWeight: 700, fontSize: compact ? '11.5px' : '14px', cursor: 'pointer',
              fontFamily: theme.fontDisplay, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px'
            }}
          >
            {compact ? 'Deals' : 'View Deals'}<TagIcon size={compact ? 11 : 14} />
          </button>
        </div>
      )}

      {/* Freeze this price (Aug 31, per Ali's report — Phase 4 fix: this
          condition previously still required pin.is_urgent && check_count
          >= 10, matching the OLD backend rule — but routes/freezes.py's
          eligibility check was already simplified to pin_is_deal alone
          back in Phase 2. Left unfixed here, this button would have
          silently become unreachable the moment Mark-Urgent was removed
          from the UI, since is_urgent could never be set again by
          anyone. Now matches the real backend rule exactly: pin_is_deal
          + no existing active freeze, nothing else.) Hotel pins don't
          support Price Freeze, so this never renders on those.
          is_active check added (Sep 15, per Ali's report) — see the
          View Deals button above for the full reasoning; same bug,
          same fix, same button pair. departure_needs_new_date check
          added alongside it (Sep 20) — same reasoning as View Deals
          above, this is the actual button whose absence of this guard
          caused the Glasgow bug. freeze_runway_ok check added (Sep 21,
          per Ali's decision on the minimum-runway floor) — mirrors
          create_freeze's own new server-side check (routes/
          freezes.py): departure must be at least
          freeze_min_runway_hours away, or there's no real monitoring
          window left to sell. */}
      {/* Per-card trial offer REMOVED (Sep 24, per Ali's report,
          approved mockup) — the free trial is account-wide, one-time,
          not per-pin, so showing "Try free for 24h" on every single
          qualifying card implied more availability than genuinely
          exists. It now lives in one place only: the "Try Price Watch
          free" button in the top nav, which opens a picker over
          exactly these same deal-ready pins. This card goes back to
          being a single, always-the-same button. */}
      {/* !evidenceEligibleFreezesByPin[pin.id] added (Sep 24, per
          Ali's report) — same reasoning as dealReadyPins' own filter
          above: a pin can render via a path other than dealReadyPins
          membership, so this per-card condition needs its own copy of
          the same exclusion, not just a fix at the list level. */}
      {pin.is_active !== false && !pin.departure_needs_new_date && pin.freeze_runway_ok !== false && pin.pin_type !== 'hotel' && pinIsDeal(pin) && !activeFreezesByPin[pin.id] && !evidenceEligibleFreezesByPin[pin.id] && (
        <div style={{ marginTop: compact ? '6px' : '8px' }}>
          <button
            onClick={() => handleFreezeFromDashboard(pin)}
            disabled={freezingPinId === pin.id}
            style={{
              width: '100%',
              background: freezingPinId === pin.id ? theme.inactiveGray : theme.structural,
              color: freezingPinId === pin.id ? theme.textMuted : theme.cream,
              border: 'none',
              borderRadius: '12px',
              padding: compact ? '8px' : '10px 14px',
              fontSize: compact ? '11px' : '13px',
              fontWeight: 700,
              cursor: freezingPinId === pin.id ? 'default' : 'pointer'
            }}
          >
            {freezingPinId === pin.id ? 'Starting… about 10 seconds' : 'Start a Price Watch'}
          </button>
          {freezeErrorByPin[pin.id] && (
            <p style={{ fontSize: '12px', color: theme.danger, marginTop: '4px', marginBottom: 0 }}>
              {freezeErrorByPin[pin.id]}
            </p>
          )}
        </div>
      )}

      {/* Details expand removed (Aug 16, per Ali's report) — every
          field it showed for flight pins duplicates something already
          visible elsewhere on this card (Route/Budget above, dates/
          passengers in the itinerary summary line below), the one real
          non-redundant piece (the currency-mismatch warning) now shows
          inline instead of being buried behind a tap, and hotel pins'
          check-in/out/room info moved into renderItinerarySummaryLine
          above rather than being deleted along with the rest of this. */}
    </>
  );

  // New home for Edit/Delete — moved out of the Details row above and
  // down to sit right beneath the itinerary summary line (replacing
  // the old Hotel/Car/Insurance row's position), per the reviewed
  // mockup.
  // Urgent toggle relocated here (Aug 18, per Ali's report — fixing
  // the cramped-stub layout bug) from the stub's primary action row,
  // where it used to sit as a squeezed icon-only button next to "View
  // Deals" whenever a pin qualified as both. A pin that's ALREADY a
  // deal has "View Deals" as its one stub button now (see
  // renderActionsAndLinks), which would have left no way at all to
  // mark it urgent — this is that control, moved to where there's
  // actual room instead of removed.
  const renderEditDeleteLinks = (pin, compact = false) => (
    <div style={{ display: 'flex', gap: compact ? '8px' : '16px', marginTop: compact ? '6px' : '8px', fontSize: compact ? '10px' : '12px', alignItems: 'center', flexWrap: 'wrap' }}>
      {/* Active-freeze guard (Sep 9, per Ali's report) — a pin with a
          genuinely active freeze should never show "Lifetime check
          limit reached," regardless of what is_active happens to say.
          Only affects the specific contradictory case (is_active=false
          alongside an active freeze) — a normal actively-frozen pin
          (is_active=true) already correctly falls into the Edit/Delete
          branch below unchanged, since the check requires BOTH
          conditions, not just the freeze alone. */}
      {pin.is_active === false && !activeFreezesByPin[pin.id] ? (
        // Deactivated pin (Aug 23, per Ali's report) — no Edit/Mark
        // Urgent/Delete here at all; none apply to a pin that's not
        // currently tracking, and "delete" on an already-inactive pin
        // would just be a no-op re-setting the same fields, not a real
        // additional action — so it's genuinely nothing to offer, not a
        // disabled button. lifetime_cap_reached decides the one real
        // choice available: Reactivate, or nothing (a truly spent pin
        // gets no action here at all — "pin a new trip" is the real next
        // step, not implied by anything on this card).
        <>
          {/* View link (Sep 15, per Ali's report — real gap: DealScreen
              got real inactive-pin handling (see its own
              showInactiveNotice) but nothing on this card actually
              linked there, so it had no way to be reached at all.
              Gated on check_count > 0, same condition the "X checks"
              badge elsewhere on this card already uses — a pin
              deactivated before its first real check has no deal data
              for DealScreen to show yet.
              !evidenceEligibleFreezesByPin guard added (Sep 22, per
              Ali's report — real, confirmed redundancy: a pin in its
              Submit Proof window already gets its own "Review →" link
              (below, via the evidence card) to FreezeDetailScreen,
              which shows everything DealScreen's View would — the
              itinerary, the price chart, the full day-by-day timeline
              — plus the freeze-specific outcome DealScreen doesn't
              have at all. Two links to two different screens showing
              overlapping information was just noise; View stays for
              its actual purpose, a genuinely paused free pin with no
              freeze involved. */}
          {(pin.check_count || 0) > 0 && !evidenceEligibleFreezesByPin[pin.id] && (
            <span
              onClick={() => navigate(pin.pin_type === 'hotel' ? '/hotel-deal' : '/deal', { state: { pin } })}
              style={{ color: theme.action, cursor: 'pointer', fontWeight: 700 }}
            >
              View →
            </span>
          )}
          {pin.lifetime_cap_reached ? (
            <span style={{ color: theme.textMuted, fontStyle: 'italic' }}>Lifetime check limit reached — pin a new trip to keep tracking this route</span>
          ) : pin.pause_reason === 'inactivity' ? (
            // New branch (Sep 23) — a pin paused for inactivity is
            // NOT the same as a departure-passed one: same dates are
            // still valid, so this skips the ReactivateDateModal
            // entirely and calls handleResumePin directly.
            <button
              onClick={() => handleResumePin(pin)}
              style={{ fontSize: '12px', padding: '6px 12px', background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 700 }}
            >
              Still watching — resume
            </button>
          ) : (
            <button
              onClick={() => {
                // Was pin.deactivation_reason === 'departure_passed'
                // (Sep 20, per Ali's report — the Jomo Kenyatta case:
                // deactivation_reason says WHY a pin stopped, not
                // whether its DATE is still stale. A deleted or
                // legacy-null pin can be just as date-stale as a
                // departure_passed one, and the old check missed
                // those entirely — reactivating went straight through
                // with no date prompt, leaving the exact contradictory
                // "active but still shows the old past date" state
                // this whole flow was built to prevent. Now uses
                // departure_date_stale — the same underlying
                // needs_new_date computation trips.py already used,
                // just exposed un-gated by is_active so it's visible
                // on a still-paused pin, before the click, instead of
                // only becoming visible (and too late to act on)
                // right after.
                if (pin.departure_date_stale) {
                  setReactivateDateError(null);
                  setReactivateDatePin(pin);
                } else {
                  handleReactivatePin(pin);
                }
              }}
              style={{ fontSize: '12px', padding: '6px 12px', background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: 700 }}
            >
              ↻ Reactivate
            </button>
          )}
        </>
      ) : (
        <>
          <span onClick={() => handleEditPin(pin)} style={{ color: theme.textMuted, cursor: 'pointer' }}>Edit</span>
          <span onClick={() => handleDeletePin(pin)} style={{ color: theme.textMuted, cursor: 'pointer' }}>Delete</span>
        </>
      )}
    </div>
  );

  // Replaces the old Hotel/Car/Insurance row — those three links were
  // placeholder cross-sell (see renderCrossSellRow's own history), a
  // permanent fixture regardless of whether they were ever useful to a
  // given pin. This is real, always-relevant information about the
  // pin itself instead: dates, passengers, and which fare type this
  // pin tracks (see flight_preference) — the same facts previously
  // buried one tap away in the expandable Details panel, now visible
  // at a glance.
  const renderItinerarySummaryLine = (pin, compact = false) => {
    // Hotels used to return null here and rely entirely on the Details
    // expand panel for check-in/out and room info (Aug 16, per Ali's
    // report — removing that panel as genuinely redundant for flight
    // pins, whose Route/dates/passengers already show elsewhere on the
    // card). Hotels had no such duplication — this was the only place
    // that info appeared at all, so it moves here instead of being
    // deleted along with the rest of that panel.
    if (pin.pin_type === 'hotel') {
      const nights = (pin.check_in_date && pin.check_out_date)
        ? Math.round((new Date(pin.check_out_date) - new Date(pin.check_in_date)) / 86400000)
        : null;
      const dateStr = (pin.check_in_date && pin.check_out_date)
        ? formatTravelDateRange(pin.check_in_date, pin.check_out_date)
        : 'Dates not set';
      const roomsLabel = `${pin.num_rooms || 1} room${(pin.num_rooms || 1) !== 1 ? 's' : ''}`;
      const guestsLabel = `${pin.num_guests || 1} guest${(pin.num_guests || 1) !== 1 ? 's' : ''}`;
      return (
        <div style={{
          background: theme.bg, borderRadius: '12px', padding: compact ? '8px 10px' : '10px 12px',
          marginTop: compact ? '10px' : '14px', fontSize: compact ? '10.5px' : '11.5px', color: theme.structural, fontWeight: 600,
          display: 'flex', flexWrap: 'wrap', gap: compact ? '4px 10px' : '6px 14px'
        }}>
          <span>{dateStr}{nights ? ` (${nights} night${nights !== 1 ? 's' : ''})` : ''}</span>
          <span>{roomsLabel}, {guestsLabel}</span>
        </div>
      );
    }
    const dateStr = pin.departure_date
      ? formatTravelDateRange(pin.departure_date, pin.return_date)
      : (pin.travel_month || 'Dates not set');
    const flexLabel = pin.date_flexible ? ' (±3 days)' : '';
    const paxLabel = `${pin.adults || 1} adult${(pin.adults || 1) !== 1 ? 's' : ''}${pin.child_ages && pin.child_ages.length > 0 ? `, ${pin.child_ages.length} child${pin.child_ages.length !== 1 ? 'ren' : ''}` : ''}`;
    const prefLabel = pin.flight_preference === 'direct' ? 'Direct only' : 'Best value';

    return (
      <div style={{
        background: theme.bg, borderRadius: '12px', padding: compact ? '8px 10px' : '10px 12px',
        marginTop: compact ? '10px' : '14px', fontSize: compact ? '10.5px' : '11.5px', color: theme.structural, fontWeight: 600,
        display: 'flex', flexWrap: 'wrap', gap: compact ? '4px 10px' : '6px 14px'
      }}>
        <span>{dateStr}{flexLabel}</span>
        <span>{paxLabel}</span>
        <span>{prefLabel}</span>
      </div>
    );
  };


  // whichever card renders it. White text throughout — the vibrant
  // category colors (green/amber/blue/red) don't have enough contrast
  // for dark text the way pale badge fills normally would.
  //
  // Sized down from the first pass — the original 11px/5-13px padding
  // read as too large and heavy against the actual card, not just in
  // the mockup.
  const renderRibbon = (pin) => {
    const ribbon = activeFreezesByPin[pin.id] ? null : pickRibbon(pin);
    if (!ribbon) return null;

    // Don't show an exciting ribbon on top of data we've separately
    // flagged as stale (the "verify before booking" warning below) —
    // pickRibbon has no concept of check-recency at all, it only knows
    // price vs. budget, so it can and did confidently claim "Rare find"
    // on a price we're simultaneously telling the user not to fully
    // trust yet. The ribbon returns automatically once a fresh check
    // comes back and clears the warning.
    if (getFreshnessState(pin.last_checked).warn) return null;

    return (
      <div style={{
        position: 'absolute', top: '12px', left: '-34px', width: '110px', zIndex: 2,
        transform: 'rotate(-45deg)', textAlign: 'center',
        background: ribbon.color, color: ribbon.textColor || '#ffffff', fontSize: '12px', fontWeight: 700,
        padding: '5px 0', fontFamily: theme.fontDisplay,
        textTransform: 'uppercase', letterSpacing: '0.02em',
        boxShadow: '0 3px 8px rgba(0,0,0,0.25)'
      }}>
        <span style={{ fontSize: '12px', marginRight: '3px' }} role="img" aria-hidden="true">{ribbon.icon}</span> {ribbon.label}
      </div>
    );
  };

  // Budget suggestion box — was capped at SO_CLOSE_MAX_PCT_OVER (25%),
  // matching the ribbon's own "So Close" threshold. Uncapped (Sep 15,
  // per Ali's report — real point: past 25% over, a pin still showed
  // the plain "$X over" stub number but lost this actionable box
  // entirely, right when a clear next step mattered just as much,
  // arguably more. Shows for any amount over budget now, worded the
  // same way regardless of how far over — the amount itself already
  // communicates how big an ask it is, without needing two different
  // copy styles above/below an arbitrary line).
  const renderBudgetSuggestion = (comparable, budget, budgetCurrency) => {
    if (comparable == null || !budget || comparable <= budget) return null;
    const deltaNeeded = Math.ceil(comparable - budget);
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px',
        background: theme.action, borderRadius: '12px', padding: '9px 12px'
      }}>
        <span style={{ fontSize: '12px', fontWeight: 700, color: theme.structural }}>
          Raise budget by {deltaNeeded} {budgetCurrency} to get this deal
        </span>
      </div>
    );
  };

  // Hotels/car rental/insurance cross-sell row — removed (Aug 13).
  // Replaced by renderItinerarySummaryLine above: real, always-useful
  // pin information (dates, passengers, fare type) instead of a
  // permanent placeholder-links row. buildHotelSearchUrl/
  // buildCarRentalUrl/buildInsuranceUrl remain in utils/dealExtras.js
  // in case any other screen still uses them.

  // Attached beneath an urgent card's own rounded/overflow-hidden
  // wrapper as one visual unit (Option B from the mockup review) —
  // only urgent, deal-qualifying pins can ever have an active freeze
  // (see routes/freezes.py's eligibility check), so this only needs
  // wiring into renderUrgentCard, never renderRegularCard.
  const renderFreezeBanner = (pin) => {
    const freeze = activeFreezesByPin[pin.id];
    if (!freeze) return null;

    const startedAt = new Date(freeze.started_at);
    const expiresAt = new Date(freeze.expires_at);
    const now = new Date();
    const elapsedMs = Math.min(Math.max(now - startedAt, 0), expiresAt - startedAt);
    // Duration-aware (Sep 9, per Ali's report — real bug, caught live
    // via screenshot: a 48-hour freeze still showed "Day 1 of 5" on
    // this Dashboard banner, same class of bug already fixed on
    // FreezeDetailScreen.js, just a second, separate place it was
    // hardcoded). Falls back to 120 for any freeze created before
    // duration_hours existed as a column, matching that same fallback.
    const durationDays = (freeze.duration_hours || 120) / 24;
    const dayOfFive = Math.min(durationDays, Math.max(1, Math.ceil(elapsedMs / (24 * 60 * 60 * 1000)) || 1));

    return (
      <div
        onClick={() => navigate('/freeze', { state: { freezeId: freeze.id, userId: currentUserId } })}
        style={{
          background: theme.structural,
          padding: '11px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', position: 'relative', overflow: 'hidden'
        }}
      >
        <div className="freeze-banner-shimmer" />
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', position: 'relative', zIndex: 2 }}>
          <span style={{ fontSize: '18px' }}>⏱</span>
          <div>
            <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#fff' }}>
              Price Watch · Starting price {formatMoney(freeze.frozen_price)} {freeze.currency}
            </div>
            <div style={{ fontSize: '12px', color: '#B9CBE0', opacity: 0.9 }}>
              Day {dayOfFive} of {durationDays} · checked every 2 hours
            </div>
          </div>
        </div>
        <span style={{ fontSize: '12px', fontWeight: 700, color: '#fff', background: 'rgba(255,255,255,0.15)', padding: '5px 10px', borderRadius: '999px', position: 'relative', zIndex: 2 }}>
          View →
        </span>
      </div>
    );
  };

  // Companion to renderFreezeBanner, for the resolved side of the same
  // freeze's life (see evidenceEligibleFreezesByPin above for the full
  // reasoning on why this needs to exist at all). Deliberately its own
  // function rather than a branch inside renderFreezeBanner — an active
  // freeze and a resolved-kept freeze are different states with
  // different messaging and a different navigate() destination-intent
  // (see a live freeze vs. review evidence on arrival at
  // FreezeDetailScreen), not two colors of the same banner. Uses
  // theme.action (signal yellow) rather than theme.structural (navy,
  // what the active banner uses) since this genuinely is a
  // time-boxed action item — "you have 48 hours to do something" — not
  // a passive status update like "still watching, Day 3 of 5."
  const renderEvidenceBanner = (pin) => {
    const freeze = evidenceEligibleFreezesByPin[pin.id];
    if (!freeze) return null;

    const hoursLeft = Math.max(0, Math.ceil(48 - (Date.now() - new Date(freeze.resolved_at).getTime()) / 3600000));

    return (
      <div
        onClick={() => navigate('/freeze', { state: { freezeId: freeze.id, userId: currentUserId } })}
        style={{
          background: theme.action,
          padding: '11px 18px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer'
        }}
      >
        <div>
          <div style={{ fontSize: '12.5px', fontWeight: 700, color: '#1a1a1a' }}>
            Paid more than {freeze.frozen_price} {freeze.currency}? Submit proof
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(26,26,26,0.75)' }}>
            {hoursLeft > 0 ? `${hoursLeft}h left to submit` : 'Window closing soon'}
          </div>
        </div>
        <span style={{ fontSize: '12px', fontWeight: 700, color: '#1a1a1a', background: 'rgba(26,26,26,0.12)', padding: '5px 10px', borderRadius: '999px' }}>
          Review →
        </span>
      </div>
    );
  };


  // three-tier reorganization). Replaces the old renderUrgentCard/
  // renderRegularCard split entirely: every pin now gets the exact same
  // photo-header treatment regardless of which tier it's in, which is
  // what actually fixes the "these look abandoned" problem flagged
  // directly from a live screenshot — the old renderRegularCard never
  // got the photo redesign applied to it, so non-urgent pins visibly
  // lagged behind urgent ones in polish. tier is 'urgent' | 'freeze' |
  // 'quiet' and only changes the badge row — everything else (price
  // logic, currency-mismatch warning, itinerary line, freeze banner) is
  // identical across all three, because it should be: a pin's tier
  // doesn't change whether its data is trustworthy.
  const renderPinCard = (pin, tier) => {
    const label = pinTitle(pin);
    const { price: comparable, available: hasComparable, isConverted } = comparablePrice(pin);
    const priceCurrency = priceCurrencyLabel(pin);
    const budgetCurrency = pin.currency || 'CAD';
    // Rewritten (Aug 31, per Ali's report — Phase 4 of the new pin
    // model, replacing the old urgent/freeze/quiet/paused 4-state
    // system now that Urgent no longer exists). tier is 'deal' |
    // 'freeze' | 'watching' | 'paused', set once by the caller
    // (renderTierSection below) based on which real bucket the pin is
    // in — this function no longer re-derives is_urgent or over-budget
    // status itself, since pinIsDeal already fully decides "deal" vs
    // "watching" before this even runs.
    let stubTone = 'quiet';   // 'deal' (yellow) | 'freeze' (navy) | 'quiet' (outline) | 'paused' (muted grey)
    let stubLabel = 'Tracking';
    if (tier === 'freeze') {
      stubTone = 'freeze';
      stubLabel = 'Price Watch';
    } else if (tier === 'deal') {
      stubTone = 'deal';
      stubLabel = 'Deal ready';
    }
    // Paused state overrides whatever tier the pin was in (Aug 23, per
    // Ali's report) — a deactivated pin's badge should read as "not
    // currently tracking," not still carry a "Deal ready" or "Frozen"
    // label that's no longer true the moment is_active is false.
    //
    // Evidence-window exception (Sep 8, per Ali's report) — real bug,
    // confirmed via an actual screenshot: a pin whose freeze just
    // resolved 'kept' can be is_active=false (Duffel checks correctly
    // stopped, nothing left to track) while STILL sitting inside its
    // real 48-hour evidence-submission window — renderEvidenceBanner
    // below was already correctly showing "30h left to submit" on
    // this exact card, while this stub simultaneously flattened the
    // same pin to muted grey "Limit reached" right above it, which
    // reads as "nothing left to do here" the moment someone glances at
    // the corner badge, directly contradicting the real, time-boxed
    // action item the banner below is asking for. No new visual tone
    // invented for this — reuses 'deal' (theme.action, signal yellow),
    // the same color the evidence banner below already uses for
    // exactly this "time-boxed action item" reasoning, so the badge
    // and the banner agree with each other instead of one muting what
    // the other is actively asking for.
    const hasOpenEvidenceWindow = !!evidenceEligibleFreezesByPin[pin.id];
    // Active-freeze guard (Sep 9, per Ali's report — real, alarming-
    // looking bug caught live via screenshot: a pin showed "Limit
    // reached" in its corner badge while simultaneously showing "Price
    // frozen — Day 3 of 5, watching for updates" in the banner below,
    // on the same card. Traced both backend safeguards that should
    // prevent is_active=false and an active freeze from ever
    // coexisting (create_freeze blocks freezing an inactive pin;
    // price_checker.py's lifetime-cap deactivation exempts any pin
    // with an active freeze) — both are genuinely correct, so this
    // specific case was likely inherited from accumulated test-cycle
    // state on a heavily-reused pin, not a fresh reproduction of a
    // live bug. But the frontend had no defense against it either way
    // — this check adds one: if activeFreezesByPin says a freeze is
    // genuinely still running, that's always the more relevant, truer
    // signal than a stale or contradictory is_active flag, so the
    // muted "Limit reached"/"Paused" override never applies while one
    // exists, regardless of how the data got into that state.
    const hasActiveFreezeOverride = !!activeFreezesByPin[pin.id];
    if (hasActiveFreezeOverride) {
      // Leave stubTone/stubLabel at their normal 'freeze' default —
      // do not apply either muted override below.
    } else if (hasOpenEvidenceWindow) {
      // `pin.is_active === false &&` dropped (Sep 24, per Ali's
      // report — real bug, caught live via screenshot: the corrected
      // retirement design now keeps a real 'kept' charge's pin
      // genuinely is_active=true through its whole 48h Submit-Proof
      // window (see freeze_resolver.py's retire_pins_past_evidence_
      // window) — so this branch, written back when the OLD design
      // required is_active=false for the SAME window, silently never
      // fired for it anymore. Fell through to the plain 'Tracking'
      // default instead, which is exactly what the screenshot showed:
      // "TRACKING 508.87 SAR" sitting right next to a Submit Proof
      // banner asking for exactly the opposite read — checking had
      // already stopped, there was nothing left being "tracked."
      // hasOpenEvidenceWindow alone (the same freeze-data-driven
      // signal evidenceEligibleFreezesByPin/dealReadyPins/the per-card
      // button all already use) is the correct, sufficient condition
      // now — true regardless of is_active, and it still correctly
      // catches any old stuck pin from before this fix too.
      stubTone = 'deal';
      stubLabel = 'Submit proof';
    } else if (pin.is_active === false) {
      stubTone = 'paused';
      stubLabel = pin.lifetime_cap_reached ? 'Limit reached' : 'Paused';
    }
    // Card border/fade follow the same rule as the badge above (Oct 10): a
    // live watch or an open proof window means the card is NOT paused,
    // even if is_active is stale-false.
    const cardPaused = pin.is_active === false && !hasActiveFreezeOverride && !hasOpenEvidenceWindow;
    const stubBg = stubTone === 'deal' ? theme.action : stubTone === 'freeze' ? theme.structural : stubTone === 'paused' ? theme.border : theme.quietStub;
    const stubText = stubTone === 'deal' ? '#1a1a1a' : stubTone === 'paused' ? theme.textMuted : '#fff';
    const stubSubText = stubTone === 'deal' ? 'rgba(26,26,26,0.7)' : (stubTone === 'freeze' ? '#B9CBE0' : stubTone === 'paused' ? theme.textMuted : '#E4EBF2');

    return (
      <div key={pin.id} id={`pin-card-${pin.id}`} style={{ position: 'relative', height: '100%', transition: 'box-shadow 0.4s ease', boxShadow: highlightPinId === pin.id ? `0 0 0 3px ${theme.action}` : 'none' }}>
        <div className={stubTone === 'deal' ? 'urgent-card-pulse' : undefined} style={{
          background: theme.surface,
          border: cardPaused ? `2px solid ${theme.textMuted}` : (stubTone === 'deal' ? `2px solid ${theme.action}` : '1px solid #DDE3E7'),
          boxShadow: '0 2px 10px rgba(30,58,95,0.07)',
          borderRadius: '16px',
          overflow: 'hidden',
          position: 'relative',
          // height/flex added (Sep 19, per Ali's report — real bug,
          // caught live from a screenshot: the two frozen cards in the
          // same grid row didn't line up at the bottom, because the
          // Auckland card had extra content (the new departure-
          // reminder banner) pushing its whole content area taller,
          // while São Paulo's card just stayed its own natural,
          // shorter height. pin-card-grid's CSS grid already stretches
          // each CELL to match the row's tallest item (align-items:
          // stretch is the grid default) — the missing piece was that
          // nothing inside actually filled that stretched cell. This
          // fixes it structurally, not just for this one banner:
          // whatever varies a card's content length in the future
          // (a longer destination name, a different badge), the
          // bottom stub/banner group below will still land at the
          // same Y position as its row sibling, via marginTop: 'auto'
          // on that group rather than shortening text to fit.
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          opacity: cardPaused ? 0.7 : 1
        }}>
          {renderRibbon(pin)}
          {/* Photo strip — full width, destination name only now; price
              moved into the stub below where the boarding-pass structure
              actually wants it. Kept full-width rather than squeezed into
              the narrower main ticket column, since a cropped photo in a
              ~200px column reads worse than one spanning the card. */}
          <PinCardPhotoHeader destination={pin.pin_type === 'hotel' ? pin.location : pin.destination}>
            <div style={{
              fontFamily: theme.fontDisplay, fontSize: '17px', fontWeight: 700, color: theme.cream,
              display: 'flex', alignItems: 'center', gap: '8px',
              paddingLeft: (activeFreezesByPin[pin.id] ? null : pickRibbon(pin)) ? '54px' : 0, minWidth: 0
            }}>
              {label}
            </div>
          </PinCardPhotoHeader>

          {/* Ticket body — main (chip row + budget/meta + itinerary) |
              perforation | stub (price + status + primary action). This
              is the actual boarding-pass structure from the approved
              reference, not just a recolor. */}
          <div className="ticket-row">
            <div style={{ flex: 1, minWidth: 0, padding: '14px 16px' }}>
              {renderPromptBanners(pin)}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                {(pin.check_count || 0) > 0 && (
                  <span style={{ fontSize: '9.5px', background: theme.bg, color: theme.textMuted, padding: '3px 8px', borderRadius: '999px', fontWeight: 700, border: `1px solid ${theme.divider}` }}>
                    {pin.check_count} checks{pinIsLowestYet(pin) ? ' · lowest yet' : ''}
                  </span>
                )}
                {/* Resolved-freeze badge (Sep 4, per Ali's report) —
                    only shown once the pin has no active freeze (a
                    currently-freezing pin already gets its own navy
                    "Frozen" stub color, no need to double up). Colors
                    match the exact convention Purchase History already
                    established: kept = navy, refunded = green.
                    Persists indefinitely per Ali's decision, rather
                    than fading after a few days. */}
                {/* Real bug found live (Sep 8, per Ali's report) — this
                    chip only ever checked freeze.status, which never
                    changes to "refunded" for an evidence-approved
                    refund by design (see _apply_evidence_decision's own
                    comment: the automatic Day-5 decision was correct,
                    so status correctly stays "kept" — only
                    payment_status changes, to
                    'kept_then_refunded_by_evidence'). Result: a freeze
                    that Ali had just personally approved a real Stripe
                    refund for was still showing plain navy "Freeze
                    kept" on the Dashboard, with nothing anywhere on the
                    card reflecting that a refund had actually happened
                    — confirmed correct on Payment History (which
                    already checked payment_status) at the exact same
                    time this chip was showing the stale label. Same
                    fix pattern, same "Refunded — paid more" wording,
                    reused verbatim rather than inventing new copy for
                    the same real-world outcome. */}
                {tier !== 'freeze' && resolvedFreezesByPin[pin.id] && (
                  <span
                    onClick={(e) => { e.stopPropagation(); navigate('/freeze', { state: { freezeId: resolvedFreezesByPin[pin.id].id, userId: currentUserId } }); }}
                    style={{
                      fontSize: '9.5px', fontWeight: 700, padding: '3px 8px', borderRadius: '999px', cursor: 'pointer',
                      background: OUTCOME_STYLE[watchOutcome(resolvedFreezesByPin[pin.id].status, resolvedFreezesByPin[pin.id].payment_status, resolvedFreezesByPin[pin.id].fee_amount)].bg,
                      color: OUTCOME_STYLE[watchOutcome(resolvedFreezesByPin[pin.id].status, resolvedFreezesByPin[pin.id].payment_status, resolvedFreezesByPin[pin.id].fee_amount)].fg
                    }}
                  >
                    {resolvedFreezesByPin[pin.id].payment_status === 'kept_then_refunded_by_evidence'
                      ? 'Watch: fee refunded (paid more)'
                      : (resolvedFreezesByPin[pin.id].status === 'kept' ? 'Watch: fee charged' : 'Watch: you paid nothing')}
                  </span>
                )}
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: theme.textPrimary, fontFamily: theme.fontMono }}>Budget: {pin.budget} {budgetCurrency}</p>
              {/* Restored (Aug 18, per Ali's report — a real regression):
                  this exact messaging existed in renderPriceBlock before
                  it was deleted for having its only caller (the Details
                  expand panel) removed — but renderPriceBlock was quietly
                  doing double duty, and this status explanation never got
                  carried into the new unified card. Without it, a pin
                  with no successful price ever recorded showed a bare
                  "..." with zero explanation of why — confirmed from live
                  screenshots of two pins in exactly this state. The
                  no_flights/no_availability case specifically calls out
                  Direct only as a likely cause, since that preference is
                  locked at pin creation (see PinTrip.js) and a route with
                  no real nonstop service will return zero results on
                  every single check, forever, with no way to fix it
                  short of deleting and recreating the pin. */}
              {!pin.last_price && (
                <p style={{ margin: '6px 0 0', fontSize: '12px', color: theme.textMuted }}>
                  {pin.last_check_status === 'no_flights' || pin.last_check_status === 'no_availability' ? (
                    <>
                      No {pin.pin_type === 'hotel' ? 'availability' : 'flights'} found yet.
                      {pin.flight_preference === 'direct' && (
                        <> This pin only tracks direct flights — if none exist for this route, it may never find a result.</>
                      )}
                    </>
                  ) : pin.last_check_status === 'error' && (pin.consecutive_failures || 0) >= 3 ? (
                    <span style={{ color: theme.danger, fontWeight: 600 }}>⚠ Trouble checking this route — we'll keep retrying.</span>
                  ) : (pin.check_count || 0) > 0 && pin.last_check_status === 'ok' && pin.flight_preference === 'direct' ? (
                    // Oct 19, external tester report: a direct-only pin whose
                    // check found flights but none nonstop is saved by the
                    // backend with last_price = null and status "ok", which
                    // used to fall through to "Checking for prices..." forever.
                    // Now it shows the alternative that was found, with a
                    // one-tap switch (same rules as the Deal screen's button).
                    (() => {
                      const alt = getAltFare(pin, 1);
                      const altCurrency = pin.last_price_currency || budgetCurrency;
                      return (
                        <>
                          No nonstop flights found yet.
                          {alt ? (
                            <>
                              {' '}Cheapest with {alt.stops} stop{alt.stops === 1 ? '' : 's'}: {formatMoney(alt.price)} {altCurrency}.
                              {canSwitchFare(pin, false) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); setSwitchFarePin({ pin, alt, altCurrency }); }}
                                  style={{ display: 'block', marginTop: '8px', padding: '8px 12px', background: theme.action, color: theme.structural, border: `2px solid ${theme.structural}`, borderRadius: '0px', fontWeight: 800, fontSize: '12.5px', cursor: 'pointer' }}
                                >
                                  Track flights with stops instead
                                </button>
                              )}
                            </>
                          ) : (
                            <> We'll keep watching.</>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    'Checking for prices...'
                  )}
                </p>
              )}
              {/* Preserved from renderPriceBlock's !available branch (Aug
                  16, per Ali's report — removing the redundant Details
                  expand this lived inside): the ONE place on this card
                  that warned when a currency mismatch makes the price
                  genuinely uncomparable — not filler, a real safety
                  warning. */}
              {!hasComparable && pin.last_price && pin.last_price_currency && pin.last_price_currency !== budgetCurrency && (
                <p style={{ margin: '6px 0 0', fontSize: '12px', fontWeight: 700, color: theme.danger }}>
                  ⚠ Priced in {pin.last_price_currency}, budget set in {budgetCurrency} — couldn't convert just now
                </p>
              )}
              {/* Suppressed while this pin has an active freeze (Sep
                  16, per Ali's report — real bug, caught live via
                  screenshot: a frozen pin already has its protection
                  locked in at the frozen price, so telling someone to
                  "raise budget to get THIS deal" — referring to a
                  newer, different, higher price a later background
                  check happened to find — is actively misleading. The
                  frozen price is what matters now, not whatever the
                  periodic comparison surfaces next.) */}
              {/* is_active check added (Sep 19, per Ali's report — real
                  bug, caught live from a screenshot: this only ever
                  excluded active-freeze pins, but a paused pin (any
                  reason — deleted or departure_passed) isn't being
                  actively compared going forward either. "Raise budget
                  to get THIS deal" pointed at a stale, no-longer-live
                  price regardless of which pause reason applied. */}
              {hasComparable && pin.is_active !== false && !activeFreezesByPin[pin.id] && renderBudgetSuggestion(comparable, pin.budget, budgetCurrency)}
              {renderItinerarySummaryLine(pin)}
              {renderEditDeleteLinks(pin)}
            </div>

            {/* Stub — price, status, primary action. The one part of
                this card that actually IS the boarding-pass metaphor;
                everything else is supporting information around it. */}
            <div className="ticket-stub" style={{ width: '150px', background: stubBg, padding: '14px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '9px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: stubSubText }}>{stubLabel}</div>
                <div style={{ fontFamily: theme.fontMono, fontWeight: 700, fontSize: '17px', color: stubText, marginTop: '3px', wordBreak: 'break-word' }}>
                  {pin.last_price
                    ? (isConverted ? formatMoney(comparable) : formatMoney(pin.best_value_price || pin.last_price))
                    : '···'}
                </div>
                {/* Fixed Sep 30, per Ali's report — real bug, caught
                    live from a screenshot: this rendered unconditionally,
                    so a pin with no price at all (no flights found yet,
                    or a brand-new pin's first check still pending) showed
                    a currency label — sometimes a wrong or stray one —
                    right next to the "···" placeholder above it, with
                    nothing to actually attach that currency to. Now only
                    renders alongside a real price, matching the guard
                    the price line itself already has. */}
                {pin.last_price && (
                  <div style={{ fontSize: '11.5px', color: stubSubText, marginTop: '2px' }}>{isConverted ? budgetCurrency : priceCurrency}</div>
                )}
                {hasComparable && pin.budget && (
                  <div style={{ fontSize: '12px', fontWeight: 700, color: stubTone === 'quiet' ? '#E4EBF2' : stubSubText, marginTop: '6px' }}>
                    {formatMoney(Math.abs(pin.budget - comparable))} {comparable <= pin.budget ? 'under' : 'over'}
                  </div>
                )}
                {/* Found-on date (Sep 7, per Ali's report) — this
                    price/itinerary genuinely gets replaced by every
                    single check, whether the new find is better or
                    worse, not just when it improves. Nothing on this
                    card ever said WHEN the current one was actually
                    found, even though that data (last_checked) already
                    existed on every pin. Deal-tier only — a paused or
                    quietly-watching pin's price isn't "a find" worth
                    dating the same way. */}
                {stubTone === 'deal' && pin.last_checked && (
                  <div style={{ fontSize: '9.5px', color: stubSubText, marginTop: '5px' }}>
                    Found {new Date(pin.last_checked).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                )}
              </div>
              {stubTone === 'freeze' ? null : (
                // Duplicate Track Freeze button removed (Aug 18, per
                // Ali's report): this stub button and renderFreezeBanner
                // below both navigated to the exact same /freeze route —
                // confirmed identical destinations, not two different
                // actions. The banner carries more real information
                // (frozen price, day count) and is already clickable as
                // one full-width unit, so it's the one link that stays;
                // the frozen tier's stub just shows the price/comparison
                // info now with no button of its own.
                renderActionsAndLinks(pin, true)
              )}
            </div>
          </div>

          <div style={{ marginTop: 'auto' }}>
            {renderFreezeBanner(pin)}
            {renderEvidenceBanner(pin)}
          </div>
        </div>
      </div>
    );
  };

  // Tier section (Aug 17, per Ali's report — the original three-tier
  // reorganization). Now used for every section on this page (Sep 8,
  // per Ali's report — the flat, unsectioned list this used to be
  // paired against is gone; once every category had its own header,
  // being the last thing left unsectioned just looked like an
  // oversight, not a deliberate simplification). Renders nothing at
  // all when a category is empty — an empty banner with nothing under
  // it would be its own small
  // version of the "abandoned" feeling this whole reorganization is
  // meant to fix, not an improvement on it.
  const renderTierSection = (label, icon, color, tierPins, tier) => {
    if (tierPins.length === 0) return null;
    // Text needs to switch to dark for the yellow/action tier specifically
    // (Aug 18, per Ali's report) — same contrast fix applied everywhere
    // else tonight that uses theme.action as a background; white text on
    // navy or slate (the other two tiers) is fine as-is.
    const badgeText = color === theme.action ? '#1a1a1a' : '#fff';
    return (
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '32px 0 16px' }}>
          <span style={{
            background: color, color: badgeText, fontSize: '13px', fontWeight: 800,
            padding: '7px 16px', borderRadius: '999px', whiteSpace: 'nowrap',
            display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            {icon ? `${icon} ` : ''}{label}
          </span>
          <span style={{ flex: 1, height: '1px', background: '#C3CFDA' }} />
          <span style={{ fontSize: '12px', color: theme.textMuted, fontWeight: 600 }}>
            {tierPins.length} {tierPins.length === 1 ? 'trip' : 'trips'}
          </span>
        </div>
        <div className="pin-card-grid">
          {tierPins.map(p => renderPinCard(p, tier))}
        </div>
      </div>
    );
  };

  // Paused trips as compact one-line rows (Oct 10, per Ali's approved
  // mockup): same information and the same Reactivate / Still watching
  // actions the old full cards had, in about a fifth of the height. Shows
  // the first 4, with a button for the rest.
  const renderPausedSection = () => {
    const list = dashboardVisiblePausedPins;
    if (list.length === 0) return null;
    const shown = showAllPaused ? list : list.slice(0, 4);
    const thisYear = new Date().getFullYear();
    const rowBtn = { fontSize: '12.5px', padding: '7px 14px', background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '999px', cursor: 'pointer', fontWeight: 800, whiteSpace: 'nowrap', fontFamily: 'inherit' };
    return (
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '32px 0 16px' }}>
          <span style={{ background: theme.textMuted, color: '#fff', fontSize: '13px', fontWeight: 800, padding: '7px 16px', borderRadius: '999px', whiteSpace: 'nowrap' }}>Paused</span>
          <span style={{ flex: 1, height: '1px', background: '#C3CFDA' }} />
          <span style={{ fontSize: '12px', color: theme.textMuted, fontWeight: 600 }}>
            {list.length} {list.length === 1 ? 'trip' : 'trips'} · tracking stopped
          </span>
        </div>
        <div style={{ background: '#fff', border: '1px solid #DDE3E7', borderRadius: '16px', boxShadow: '0 2px 10px rgba(30,58,95,0.07)', overflow: 'hidden' }}>
          {shown.map((pin, idx) => {
            const name = pinTitle(pin);
            const { price: comparable, available: hasComparable, isConverted } = comparablePrice(pin);
            const budgetCurrency = pin.currency || 'CAD';
            const dep = pin.matched_departure_date || pin.departure_date;
            const ret = pin.matched_return_date || pin.return_date;
            const dates = dep ? `${formatTravelDate(dep, thisYear)}${ret ? ` – ${formatTravelDate(ret, thisYear)}` : ''}` : '';
            const canView = (pin.check_count || 0) > 0 && !evidenceEligibleFreezesByPin[pin.id];
            const noFlights = pin.last_check_status === 'no_flights' || pin.last_check_status === 'no_availability';
            return (
              <div key={pin.id} id={`pin-card-${pin.id}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', padding: '12px 20px', borderTop: idx === 0 ? 'none' : '1px solid #EEF1F3', boxShadow: highlightPinId === pin.id ? `inset 0 0 0 3px ${theme.action}` : 'none' }}>
                <div style={{ minWidth: 0, flex: '1 1 260px' }}>
                  <span
                    onClick={canView ? () => navigate(pin.pin_type === 'hotel' ? '/hotel-deal' : '/deal', { state: { pin } }) : undefined}
                    style={{ fontWeight: 800, fontSize: '14px', color: theme.structural, cursor: canView ? 'pointer' : 'default' }}
                  >
                    {name}
                  </span>
                  <span style={{ fontSize: '12.5px', color: theme.textMuted }}>
                    {' · '}Budget {pin.budget} {budgetCurrency}{dates ? ` · ${dates}` : ''}
                    {pin.departure_needs_new_date ? ' · departure passed' : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '14px', marginLeft: 'auto', flexWrap: 'wrap' }}>
                  {hasComparable && pin.last_price ? (
                    <span style={{ fontFamily: theme.fontMono, fontSize: '12.5px', fontWeight: 700, color: theme.structural }}>
                      {formatMoney(comparable)} {isConverted ? budgetCurrency : priceCurrencyLabel(pin)}
                    </span>
                  ) : (noFlights ? <span style={{ fontSize: '12px', color: theme.textMuted }}>No flights found yet</span> : null)}
                  {pin.pause_reason === 'inactivity' ? (
                    <button onClick={() => handleResumePin(pin)} style={rowBtn}>Still watching: resume</button>
                  ) : (
                    <button
                      onClick={() => {
                        if (pin.departure_date_stale) {
                          setReactivateDateError(null);
                          setReactivateDatePin(pin);
                        } else {
                          handleReactivatePin(pin);
                        }
                      }}
                      style={rowBtn}
                    >
                      ↻ Reactivate
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {list.length > 4 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '12px 20px', borderTop: '1px solid #EEF1F3', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '12.5px', color: theme.textMuted }}>
                {showAllPaused ? `Showing all ${list.length} paused trips` : `Showing 4 of ${list.length} paused trips`}
              </span>
              <button
                onClick={() => setShowAllPaused(v => !v)}
                style={{ background: theme.structural, color: '#fff', border: 'none', borderRadius: '999px', padding: '8px 16px', fontWeight: 800, fontSize: '12.5px', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                {showAllPaused ? 'Show fewer ▴' : `Show all ${list.length} ▾`}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', paddingBottom: '40px' }}>
      <PageBackground variant="vibrant" />
      {/* Deal-found toast — only ever rendered when activeToast is set by
          the real transition-detection logic in fetchPins above. Never a
          decorative/timed element; if nothing genuinely changed, this
          renders nothing. */}
      {activeToast && (
        <div key={activeToast.key} className="deal-toast" role="status">
          <div className="deal-toast-icon">
            <BellIcon size={17} color={theme.urgentBadgeText} />
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: theme.cream }}>
              Your {activeToast.label} trip just became a deal
              {activeToast.isLowestYet && <span style={{ color: '#8FD9AE' }}> · lowest yet</span>}
            </div>
            <div style={{ fontSize: '12.5px', color: theme.cream, opacity: 0.8, marginTop: '3px' }}>
              {activeToast.mismatch
                ? `Priced in ${activeToast.rawCurrency} — budget is in ${activeToast.currency}, couldn't convert just now`
                : activeToast.isConverted
                  ? `≈ ${activeToast.price} ${activeToast.currency} — ≈ ${(activeToast.budget - activeToast.price).toFixed(2)} ${activeToast.currency} under your ${activeToast.budget} ${activeToast.currency} budget`
                  : `${activeToast.price} ${activeToast.currency} — ${(activeToast.budget - activeToast.price).toFixed(2)} ${activeToast.currency} under your ${activeToast.budget} ${activeToast.currency} budget`}
            </div>
          </div>
        </div>
      )}

      {/* Upgrade prompt — a real next step for a free-tier user who hits
          a pin-limit wall, instead of a dead-end browser alert(). No
          longer wired to anything as of Aug 31 (Phase 4) — its only
          trigger, toggleUrgent's tier-limit error, was removed along
          with Mark Urgent. Left in place as a natural reuse point for
          a future "hit your active-pin limit — buy 3 more for $5"
          prompt, rather than deleted. */}
      {/* Same warm-tone treatment as PinTrip.js's pin-limit modal
          (Sep 24, per Ali's report) — this call site never got it the
          first time, since it never passed its own title/icon and
          silently fell back to LimitModal's plain defaults ('Limit
          reached', ⚠️) for every reactivate/resume pin-limit hit.
          Genuine PIN_LIMIT_REACHED errors reuse the friendlier
          framing; anything else (a real failure) still gets the
          shared component's own default title/icon, unchanged. */}
      <LimitModal
        message={upgradePrompt}
        onClose={() => { setUpgradePrompt(null); setIsPinLimitFromReactivate(false); }}
        title={isPinLimitFromReactivate ? 'Ready for more pins?' : undefined}
        icon={isPinLimitFromReactivate ? '📌' : undefined}
        ctaLabel={isPinLimitFromReactivate ? 'Buy 3 more pins — $3' : undefined}
        onCta={isPinLimitFromReactivate ? () => { setUpgradePrompt(null); setIsPinLimitFromReactivate(false); setShowPinPurchaseModal(true); } : undefined}
      />
      <ReactivateDateModal
        pin={reactivateDatePin}
        onClose={() => setReactivateDatePin(null)}
        onSubmit={handleReactivateWithDate}
        submitting={reactivateDateSubmitting}
        error={reactivateDateError}
      />

      {/* Trial picker / empty-state prompt (Sep 24, per Ali's report,
          approved mockup) — two states in one modal, chosen by whether
          any deal-ready pin currently exists. dealReadyPins is the
          exact same array the "Deal Ready" section itself renders
          from, so this can never offer a pin that isn't genuinely
          eligible for the trial right now. */}
      {showTrialPicker && (
        <div
          onClick={() => setShowTrialPicker(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: theme.surface, border: `2px solid ${theme.structural}`, borderRadius: '0px', maxWidth: '420px', width: '100%', padding: '28px' }}
          >
            {dealReadyPins.length > 0 ? (
              <>
                <div style={{ fontSize: '17px', fontWeight: 800, color: theme.structural, marginBottom: '6px' }}>Which trip?</div>
                <div style={{ fontSize: '12.5px', color: theme.textMuted, marginBottom: '18px' }}>Your free 24-hour Price Watch — pick a trip that's currently deal-ready.</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                  {dealReadyPins.map(pin => {
                    // comparablePrice returns {price, available[, isConverted]},
                    // not a bare number — matches the destructuring
                    // pattern every other real call site in this file
                    // already uses (see e.g. the Deal Ready card's own
                    // price line above).
                    const { price, available } = comparablePrice(pin);
                    const under = available && price != null && pin.budget != null ? (pin.budget - price) : null;
                    return (
                      <div
                        key={pin.id}
                        onClick={() => { setShowTrialPicker(false); handleStartTrialFromDashboard(pin); }}
                        style={{ border: `2px solid ${theme.structural}`, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                      >
                        <div>
                          <div style={{ fontSize: '13.5px', fontWeight: 800, color: theme.structural }}>{pinTitle(pin)}</div>
                          <div style={{ fontSize: '12.5px', color: theme.textMuted, marginTop: '2px' }}>
                            {available && price != null ? `${formatMoney(price)} ${pin.currency || 'CAD'}` : ''}
                            {under != null ? ` · ${formatMoney(under)} under budget` : ''}
                          </div>
                        </div>
                        <span style={{ fontSize: '18px', color: theme.textMuted }}>→</span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, padding: '10px 12px', marginBottom: '16px' }}>
                  <p style={{ margin: 0, fontSize: '12.5px', color: theme.textMuted, lineHeight: 1.5 }}>Only pins currently at or under budget are shown — the trial checks a real, bookable price, same as a paid watch.</p>
                </div>
                <button
                  onClick={() => setShowTrialPicker(false)}
                  style={{ width: '100%', background: 'none', border: `2px solid ${theme.border}`, padding: '11px', fontSize: '13.5px', fontWeight: 700, color: theme.structural, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '12px' }}>
                <span style={{ fontSize: '34px' }}>📌</span>
                <div style={{ fontSize: '17px', fontWeight: 800, color: theme.structural }}>No deal-ready trip yet</div>
                <p style={{ margin: 0, fontSize: '13px', color: theme.textPrimary, lineHeight: 1.6 }}>Pin your first trip, and once a real fare hits your budget, you can try Price Watch free for 24 hours on it.</p>
                <button
                  onClick={() => { setShowTrialPicker(false); navigate('/pin'); }}
                  style={{ background: theme.action, color: '#1a1a1a', border: 'none', padding: '12px 28px', fontSize: '13.5px', fontWeight: 800, cursor: 'pointer', marginTop: '6px' }}
                >
                  📍 Pin a Trip
                </button>
                <span
                  onClick={() => setShowTrialPicker(false)}
                  style={{ fontSize: '12px', color: theme.textMuted, textDecoration: 'underline', cursor: 'pointer', marginTop: '2px' }}
                >
                  Maybe later
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete confirmation — reuses LimitModal's own shell (see
          handleDeletePin's comment above for the full reasoning), just
          with its own title/icon/copy instead of the limit-reached
          defaults. Cancel and the backdrop click both just clear
          confirmDeletePin — nothing happens until "Stop tracking" is
          actually pressed. */}
      <LimitModal
        message={confirmDeletePin ? `Stop tracking your pin for ${confirmDeletePin.pin_type === 'hotel' ? confirmDeletePin.location : confirmDeletePin.destination}? You can reactivate it later from the Dashboard.` : null}
        onClose={() => setConfirmDeletePin(null)}
        title="Stop tracking this trip?"
        icon="🗑️"
        ctaLabel="Stop tracking"
        onCta={executeDeletePin}
      />

      {showOnboarding && (
        <OnboardingTour
          onDone={() => {
            setShowOnboarding(false);
            markOnboardingSeen(userIdRef.current).catch(() => null);
          }}
        />
      )}

      {/* ReferralPrompt popup retired (Aug 22, per Ali's report) — it was
          a second, independent referral mechanism left over from before
          the persistent "Refer 3 friends" card (below, in the Welcome
          back panel) existed. The two disagreed with each other: this
          popup only appeared 18 seconds into a session and deliberately
          hid its own progress line at 0 referrals, while the persistent
          card is visible immediately and correct at 0 — a genuine
          conflict caught live (both rendering on screen at once, with
          different copy), not a hypothetical. The persistent card does
          this popup's job strictly better, so this is a removal, not a
          replacement — see ReferralPrompt.js, now unused but left in
          place rather than deleted outright in case any of its pieces
          (the snooze pattern, the copy logic) are worth reusing later. */}

      {/* Shared top bar (Oct 10): one component used on the Dashboard, Deal,
          Price Watch and Trip History pages. The Dashboard keeps its own
          bonus-pin chip (dismissal logic), trial pill and purchase modal. */}
      <AppNav
        current="dashboard"
        trialChip={tierLimits?.price_watch_trial_available && (
            <span
              onClick={() => setShowTrialPicker(true)}
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '999px', padding: '6px 13px', fontWeight: 800, fontSize: '12.5px' }}
            >
              Try Price Watch free
            </span>
          )}
        onBuyPins={() => setShowPinPurchaseModal(true)}
        adminItems={[
          ...(tierLimits?.is_founder ? [{
                    label: 'Admin: fix stuck pins',
                    action: async () => {
                      try {
                        const result = await fixStuckRetirements();
                        alert(`Checked ${result.checked}, fixed ${result.fixed}, left ${result.skipped_open_evidence_window} alone (still inside their real Submit-Proof window).`);
                        await fetchPins(userIdRef.current);
                      } catch (error) {
                        alert(error?.response?.data?.detail || 'Could not run the cleanup. Check that you\'re signed in as the founder account.');
                      }
                    },
                  }] : [])
        ]}
        onSignOut={handleSignOut}
      />

      {/* Top section rebuild (Aug 16/17, per Ali's report — replaces the
          old static masthead + MomentumBar + achievements pill row).
          Two distinct sections on purpose, not one merged banner:
          pinning a new trip is forward-looking intent, the insight
          banner is reactive ("something already in motion needs your
          attention") — different enough in kind that folding them into
          one card blurred the "one thing matters most" principle the
          insight banner is built around. Badge/level status moved
          entirely to the small nav-bar chip above; nothing gamification
          -related competes with real trip content down here anymore. */}
      {!showIOSInstallNudge && pins.length > 0 && (
        <>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '16px 20px 0' }}>
        <div style={{ background: '#1E3A5F', borderRadius: '18px', padding: '16px 18px 14px', boxShadow: '0 6px 18px rgba(30,58,95,0.25)' }}>
          <style>{`
            .dash-stats { display: flex; flex-wrap: wrap; align-items: center; margin-top: 12px; row-gap: 8px; }
            .dash-stat { padding: 0 18px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; font-size: 12.5px; color: #1E3A5F; }
            .dash-stat:first-child { padding-left: 0; }
            .dash-stat + .dash-stat { border-left: 2px solid #1E3A5F; }
            .dash-stat b { font-size: 14px; white-space: nowrap; }
            .dash-kicker { font-family: 'JetBrains Mono', monospace; font-size: 10px; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; }
            .dash-lbl { font-family: 'JetBrains Mono', monospace; font-size: 11.5px; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase; color: #1E3A5F; border-bottom: 3px solid #F4C11C; padding-bottom: 1px; white-space: nowrap; flex-shrink: 0; }
            .dash-details-grid { margin-top: 10px; display: grid; grid-template-columns: 1.05fr 1fr; gap: 12px; }
            @media (max-width: 860px) { .dash-details-grid { grid-template-columns: 1fr; } }
            @media (max-width: 860px) {
              .dash-stats { flex-direction: column; align-items: stretch; }
              .dash-stat, .dash-stat:first-child { padding: 9px 0; justify-content: space-between; border-left: 0; }
              .dash-stat + .dash-stat { border-top: 1px solid #9FB2C4; }
            }
          `}</style>
          {/* Dashboard panel (Oct 10, Option B) — contained to the same
              width as the trip cards (no empty sides), light blue with
              navy rules so it reads as its own zone. Leads with what
              needs attention (a status headline and the deal ticker);
              pin slots, streak and referral are one thin row beneath. */}
          {(firstName || accountEmail) && (
            <div style={{ fontFamily: theme.fontDisplay, fontSize: '15px', fontWeight: 700, color: '#C9D6E4', marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Welcome, <span style={{ color: '#fff', fontWeight: 800 }}>{firstName || accountEmail}</span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px 12px' }}>
            <span className="dash-kicker" style={{ color: '#9FB3C8' }}>Dashboard</span>
            <span style={{ fontFamily: theme.fontDisplay, fontSize: '19px', fontWeight: 800, color: '#fff' }}>
              {(() => {
                const parts = [];
                if (dealReadyPins.length > 0) parts.push(`${dealReadyPins.length} deal${dealReadyPins.length === 1 ? '' : 's'} ready`);
                if (frozenPins.length > 0) parts.push(`${frozenPins.length} Price Watch${frozenPins.length === 1 ? '' : 'es'} running`);
                return parts.length > 0 ? parts.join(' · ') : 'Your trips';
              })()}
            </span>
          </div>
          {insightItems.length > 0 && (() => {
            const item = insightItems[insightIndex % insightItems.length];
            return (
              <div
                onClick={item.onClick}
                style={{
                  background: '#fff', borderLeft: `8px solid ${theme.action}`, borderRadius: '14px', padding: '12px 14px',
                  display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 12px', cursor: 'pointer', marginTop: '10px'
                }}
              >
                {item.ribbonLabel && (
                  <span style={{ fontFamily: theme.fontMono, fontSize: '10px', fontWeight: 800, letterSpacing: '0.1em', background: theme.structural, color: theme.action, padding: '3px 9px', borderRadius: '999px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    {item.ribbonLabel}
                  </span>
                )}
                <span style={{ fontFamily: theme.fontDisplay, fontSize: '15px', fontWeight: 800, color: theme.structural }}>
                  {item.ribbonLabel ? item.label : item.fallbackHeadline}
                </span>
                {item.sub && <span style={{ fontSize: '12.5px', color: '#41586f' }}>{item.sub}</span>}
                <span style={{ flex: 1 }} />
                {insightItems.length > 1 && (
                  <span style={{ fontSize: '12px', color: '#41586f', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {(insightIndex % insightItems.length) + 1} of {insightItems.length}
                  </span>
                )}
                <span style={{ fontSize: '12.5px', fontWeight: 800, color: theme.structural, background: theme.action, padding: '8px 14px', borderRadius: '999px', whiteSpace: 'nowrap' }}>View deal →</span>
              </div>
            );
          })()}
          <div style={{ background: '#EAF1F8', borderRadius: '12px', padding: '10px 16px', marginTop: '12px' }}>
          <div className="dash-stats" style={{ marginTop: 0 }}>
            <div className="dash-stat">
              <span className="dash-lbl">Pin slots</span>
              <b>{activePinsCount} / {tierLimits ? tierLimits.max_active_pins : '—'}</b>
              <span style={{ display: 'inline-flex', width: '90px', height: '8px', background: '#fff', border: `1px solid ${theme.border}`, borderRadius: '999px', overflow: 'hidden' }}>
                {activePinsCount > 0 && tierLimits && (
                  <span style={{ width: `${Math.min(100, activePinsCount / tierLimits.max_active_pins * 100)}%`, background: theme.structural }} />
                )}
              </span>
              {tierLimits && (
                <span
                  onClick={activePinsCount >= tierLimits.max_active_pins ? () => setShowPinPurchaseModal(true) : undefined}
                  style={{ opacity: 0.8, cursor: activePinsCount >= tierLimits.max_active_pins ? 'pointer' : 'default' }}
                >
                  {activePinsCount >= tierLimits.max_active_pins ? 'None available — buy more →' : `${tierLimits.max_active_pins - activePinsCount} available`}
                </span>
              )}
              <span onClick={() => setPinDetailsOpen(o => !o)} style={{ fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {pinDetailsOpen ? 'Details ▴' : 'Details ▾'}
              </span>
            </div>
            <EngagementStrip
              tiles
              achievements={achievements}
              referralStats={referralStats}
              onShare={handleDashboardShare}
              shareLabel={dashboardReferralCopied ? 'Copied!' : 'Share invite'}
            />
          </div>
          </div>
          {pinDetailsOpen && (() => {
            const pb = tierLimits?.pin_slot_breakdown || {};
            const maxSlots = tierLimits ? tierLimits.max_active_pins : 0;
            const earned = (pb.streak || 0) + (pb.referral || 0) + (pb.purchased || 0);
            const freePlan = Math.max(0, maxSlots - earned);
            const inUsePct = maxSlots > 0 ? Math.min(100, activePinsCount / maxSlots * 100) : 0;
            const chip = { background: '#EAF1F8', borderRadius: '999px', padding: '5px 11px', fontWeight: 700, whiteSpace: 'nowrap' };
            const eqParts = [
              [freePlan, 'plan'],
              [pb.streak || 0, 'streak bonus'],
              [pb.referral || 0, 'referral bonus'],
              [pb.purchased || 0, 'bought'],
            ].filter(([n], i) => n > 0 || i === 0);
            const proofWindowActiveCount = pins.filter(p => p.is_active !== false && evidenceEligibleFreezesByPin[p.id]).length;
            const doing = [
              ['Deal ready', dealReadyPins.length, theme.action, true],
              ['Price Watch finished, proof window open', proofWindowActiveCount, theme.action, false],
              ['Price Watch running', frozenPins.length, theme.structural, true],
              ['Still watching, with prices', watchingWithDataPins.length, '#7089A8', false],
              ['Still watching, no results yet', watchingNoResultsPins.length, '#B9CBE0', false],
              ['Needs attention', needsAttentionPins.length, theme.danger, false],
            ].filter(([, n, , always]) => always || n > 0);
            const card = { background: '#fff', borderRadius: '12px', padding: '14px 16px', color: theme.structural };
            const h4 = { margin: '0 0 10px', fontFamily: theme.fontMono, fontSize: '10.5px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4A5F78' };
            return (
              <>
                <div className="dash-details-grid">
                  <div style={card}>
                    <div style={h4}>Your pin slots</div>
                    <div style={{ fontSize: '22px', fontWeight: 800 }}>
                      {activePinsCount} in use <span style={{ fontSize: '13px', fontWeight: 600, color: theme.textMuted }}>of {maxSlots || '—'}{maxSlots ? ` · ${Math.max(0, maxSlots - activePinsCount)} available` : ''}</span>
                    </div>
                    <div style={{ height: '14px', borderRadius: '999px', background: '#EAF1F8', overflow: 'hidden', margin: '8px 0 6px' }}>
                      <div style={{ width: `${inUsePct}%`, height: '100%', background: theme.structural }} />
                    </div>
                    {maxSlots > 0 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px', flexWrap: 'wrap', marginTop: '12px', fontSize: '12.5px' }}>
                        {eqParts.map(([n, label], i) => (
                          <React.Fragment key={label}>
                            {i > 0 && <span>+</span>}
                            <span style={chip}>{n} <b style={{ fontFamily: theme.fontMono }}>{label}</b></span>
                          </React.Fragment>
                        ))}
                        <span>=</span>
                        <span style={{ ...chip, background: theme.action }}><b style={{ fontFamily: theme.fontMono }}>{maxSlots} slots</b></span>
                      </div>
                    )}
                  </div>
                  <div style={card}>
                    <div style={h4}>{`What the ${activePinsCount} in use are doing`}</div>
                    {doing.map(([label, n, color]) => (
                      <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #EEF1F3', fontSize: '13px', color: label === 'Needs attention' ? theme.danger : theme.structural }}>
                        <span><span style={{ display: 'inline-block', width: '9px', height: '9px', borderRadius: '3px', marginRight: '8px', background: color }} />{label}</span>
                        <b style={{ fontFamily: theme.fontMono, fontSize: '13.5px' }}>{n}</b>
                      </div>
                    ))}
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #1E3A5F', marginTop: '4px', paddingTop: '8px', fontWeight: 800, fontSize: '13px' }}>
                      <span>Total in use</span><span>{activePinsCount}</span>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: '12px', background: '#EAF1F8', borderRadius: '12px', padding: '11px 16px', fontSize: '12.5px', color: theme.structural, display: 'flex', gap: '26px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <b>Not using a slot:</b>
                  <span><span style={{ color: theme.textMuted }}>Paused</span> <b style={{ fontFamily: theme.fontMono }}>{stillOnDashboardPins.length}</b>{stillOnDashboardPins.length > 0 ? ' · shown below on this page' : ''}</span>
                  <span>
                    <span style={{ color: theme.textMuted }}>Archived</span>{' '}
                    {archivedCount === null ? '…' : <b style={{ fontFamily: theme.fontMono }}>{archivedCount}</b>}
                    {archivedCount > 0 && <> · <span onClick={() => navigate('/history')} style={{ cursor: 'pointer', fontWeight: 800, textDecoration: 'underline' }}>see Trip History</span></>}
                  </span>
                </div>
              </>
            );
          })()}
          {notifStatus !== 'enabled' && !notifPromptSnoozed && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: '10px', padding: '0 2px' }}>
              <span
                onClick={notifStatus === 'enabling' ? undefined : handleEnableNotifications}
                style={{ fontSize: '12px', color: '#fff', cursor: notifStatus === 'enabling' ? 'default' : 'pointer' }}
              >
                {notifStatus === 'enabling'
                  ? 'Enabling...'
                  : notifStatus === 'denied'
                    ? 'Alerts blocked — check settings'
                    : "Turn on phone alerts — you're on email-only right now"}
              </span>
              <span
                onClick={dismissNotifPrompt}
                aria-label="Dismiss"
                style={{ fontSize: '12px', color: '#C9D6E4', cursor: 'pointer', fontWeight: 700 }}
              >
                ✕
              </span>
            </div>
          )}

        </div>
        </div>
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '14px 20px 0' }}>
          {/* Status breakdown — rebuilt as a segmented usage bar (Sep
              8, per Ali's report — Option A of 3 mockups, approved as
              shown). Replaces the earlier 6-tile grid, which correctly
              reconciled the math but read as "too many boxes" once
              actually live. Same underlying counts, same reconciliation
              (every active pin lands in exactly one segment; segments
              + pausedTierPins.length together account for every pin
              ever created), just one compact bar instead of six
              bordered cards. Segment widths guard against
              activePinsCount === 0 (a brand-new account with nothing
              pinned yet) to avoid a NaN%-wide segment. */}
          {/* Buy more pins — moved here from the streak/referral row
              (Sep 3, per Ali's report — real signal-mixing problem
              flagged directly from a screenshot). That row is framed
              as free loyalty rewards; putting a paid upsell alongside
              them borrowed that "nice free thing" framing for a $5
              charge, which is misleading regardless of layout. This
              stat-chip area is neutral usage status, not a rewards
              context, so a purchase prompt belongs here instead —
              same reasoning already applied to keeping the achievement
              strip separate from monetization.

              Gated + compacted (Sep 15, per Ali's report — the
              "TEMPORARY, unconditional" state below was never actually
              resolved after being flagged twice, Sep 3 and Sep 5, and
              the exact predicted problem showed up: a full card taking
              real space while sitting on 16 free slots, nowhere near
              the limit). Now only renders with 2 or fewer slots left,
              and collapses to one row — no heading, no subtext, no
              full-width button — since that's the actual moment this
              is relevant, not a permanent fixture. */}
          {/* Solid gold, full-width row (Sep 15, per Ali's report —
              the bordered/quiet version wasn't noticeable enough;
              matches the exact same theme.action fill already used on
              "+Pin Another Trip" below, so this reads as the same kind
              of real action on the page, not a third, quieter style.
              Dark text on gold everywhere else in this app already
              uses this same near-black, not theme.textPrimary's navy,
              for contrast. */}
          {tierLimits && (tierLimits.max_active_pins - activePinsCount) <= 2 && (
            <div style={{ background: theme.action, borderRadius: '14px', padding: '12px 16px', marginTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
              <span style={{ fontSize: '13px', fontWeight: 700, color: theme.structural }}>
                {activePinsCount >= tierLimits.max_active_pins
                  ? `You're at your ${tierLimits.max_active_pins}-pin limit`
                  : `${tierLimits.max_active_pins - activePinsCount} slot${tierLimits.max_active_pins - activePinsCount === 1 ? '' : 's'} left`}
              </span>
              <span
                onClick={() => setShowPinPurchaseModal(true)}
                style={{ fontSize: '13px', fontWeight: 700, color: theme.structural, whiteSpace: 'nowrap', cursor: 'pointer' }}
              >
                Buy 3 more — $3 →
              </span>
            </div>
          )}
        </div>
        </>

      )}
      {showPinPurchaseModal && currentUserId && (
        <PinBundlePurchaseModal
          userId={currentUserId}
          currency="CAD"
          onSuccess={() => {
            setShowPinPurchaseModal(false);
            setPinPurchaseSuccess(true);
            getTierLimits(currentUserId).then(setTierLimits).catch(() => null);
          }}
          onCancel={() => setShowPinPurchaseModal(false)}
        />
      )}
      {freezeDurationModalPin && (
        <FreezeDurationModal
          pinId={freezeDurationModalPin.id}
          onChoose={handleDurationChosenFromDashboard}
          onCancel={() => setFreezeDurationModalPin(null)}
          departureStopTrackingAt={freezeDurationModalPin.departure_stop_tracking_at}
        />
      )}
      {freezeModalPin && currentUserId && (
        <FreezePaymentModal
          userId={currentUserId}
          pin={freezeModalPin}
          quote={selectedQuote}
          todaysPrice={freezeModalPin.last_price_converted ?? freezeModalPin.last_price}
          resolvesAt={(() => {
            const nominal = Date.now() + selectedDurationHours * 3600000;
            const stopAt = freezeModalPin.departure_stop_tracking_at ? new Date(freezeModalPin.departure_stop_tracking_at).getTime() : null;
            return new Date(stopAt ? Math.min(nominal, stopAt) : nominal);
          })()}
          onConfirm={handleConfirmFreezeFromDashboard}
          onCancel={() => setFreezeModalPin(null)}
        />
      )}
      {pendingFreezeNav && (
        <PurchaseSuccessModal
          type="freeze"
          feeAmount={FREEZE_DURATION_TIERS.find(t => t.hours === selectedDurationHours)?.fee}
          durationLabel={FREEZE_DURATION_TIERS.find(t => t.hours === selectedDurationHours)?.label}
          onDismiss={() => navigate('/freeze', { state: pendingFreezeNav })}
        />
      )}
      {pinPurchaseSuccess && (
        // Real modal replacing the old small bottom-corner toast (Sep
        // 3, per Ali's report) — the toast just said "3 more pin slots
        // added!" with no explanation of what the $5 charge actually
        // meant or a disclaimer, unlike every other payment surface in
        // this app.
        <PurchaseSuccessModal
          type="pins"
          onDismiss={() => setPinPurchaseSuccess(false)}
        />
      )}
      {/* renderAchievements() (the detailed earned-badge pill row) is no
          longer rendered here — its summary now lives in the small nav-
          bar level chip instead (Option A from the top-section review).
          The function itself is left defined, not deleted, in case a
          future Profile/account page wants the fuller breakdown. */}

      {/* Removed: the standalone "Price Freezes" section (FreezeTracker)
          used to duplicate exactly what the gold banner under each
          urgent card (renderFreezeBanner) now shows inline — same
          frozen/live price, same day-of-5, same "watching for updates"
          state. Keeping both was redundant (flagged directly from a
          live screenshot). FreezeTracker.js itself is left alone since
          nothing else imports it — safe to delete later if it's
          confirmed to have no other use. */}

      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '20px 20px 0' }}>
        {showIOSInstallNudge && (
          <div style={{
            marginTop: '12px', background: theme.surface, border: `1px solid ${theme.border}`,
            borderRadius: '14px', padding: '12px', fontSize: '13px', color: theme.textPrimary
          }}>
            To get deal alerts on iPhone, add FlyAtlast to your Home Screen first: tap the Share icon, then "Add to Home Screen."
          </div>
        )}

        {loading && <p style={{ color: theme.textMuted }}>Loading your trips...</p>}

        {!loading && pins.length === 0 && (
          <div style={{ marginTop: '40px', textAlign: 'center', padding: '40px 20px', background: theme.surface, borderRadius: '14px', border: `1px solid ${theme.border}` }}>
            <PinIcon size={48} color={theme.structural} style={{ marginBottom: '12px' }} />
            <p style={{ color: theme.textMuted, marginBottom: '16px' }}>No trips pinned yet</p>
            <button
              onClick={() => navigate('/pin')}
              style={{ background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '14px', padding: '12px 24px', fontWeight: 800, cursor: 'pointer', fontFamily: theme.fontDisplay }}
            >
              Pin Your First Trip
            </button>
          </div>
        )}
      </div>

      {/* Hybrid layout (Aug 31, per Ali's report — Phase 4 of the new
          pin model, replacing the old four-section Urgent/Frozen/
          Quiet/Paused reorganization): one highlighted "Deal Ready"
          section for the one state actually worth calling out, then
          everything else (Frozen, Watching, Paused) as a single flat
          list below with no header — see dealReadyPins/restPins above
          for the full reasoning. */}
      {switchFarePin && (
        <SwitchFareModal
          pin={switchFarePin.pin}
          alt={switchFarePin.alt}
          currency={switchFarePin.altCurrency}
          formatMoney={formatMoney}
          onClose={() => setSwitchFarePin(null)}
          onDone={() => { setSwitchFarePin(null); if (currentUserId) fetchPins(currentUserId); }}
        />
      )}
      {renderTierSection('Deal Ready', '', theme.action, dealReadyPins, 'deal')}
      {/* Submit Proof section — new (Sep 8, per Ali's report, the
          refinement we agreed before building this). Its own headed
          section rather than folding into the flat list below, since
          this is genuinely time-sensitive (a real 48-hour countdown)
          and deserves to be found, not just technically present. Only
          ever populated by the handful of lifetime-capped pins whose
          evidence window is still open — see evidenceWindowPausedPins'
          own comment above for why this exception exists at all.
          reactivate=false is intentional: 'tier' here only decides
          which cards get a Reactivate button, and a lifetime-capped
          pin genuinely can't be reactivated regardless of what section
          it's rendered in — passing 'paused' (not a made-up new tier)
          keeps that correct without adding a tier value nothing else
          needs to know about. */}
      {renderTierSection('Submit Proof', '', theme.action, evidenceWindowPausedPins, 'paused')}
      {/* Needs Attention + Paused — added alongside Submit Proof (Sep
          8, per Ali's report — the "actionable vs. nothing-to-do"
          line, not "urgent vs. not," is what actually decides whether
          something gets a header now). Needs Attention uses danger
          red rather than theme.action gold — matches the same red
          already used for this exact state's corner badge on the
          card itself (see renderPinCard's own stubTone logic), a real
          active problem rather than a routine action, worth a
          distinct color from the other three. Paused reuses
          theme.textMuted for its accent line specifically — reactivating
          isn't urgent or wrong, gold/red would overstate it. */}
      {renderTierSection('Needs Attention', '', theme.danger, needsAttentionPins, 'watching')}
      {/* Revisit Budget — new section (Sep 15, per Ali's report — see
          revisitBudgetPins' own comment above for the full reasoning).
          Same 'watching' tier key as Needs Attention — these pins still
          have Edit/Delete as their available actions, not
          Reactivate/lifetime-message (that's only for is_active=false
          pins), so they need the active-pin action row, not the
          inactive one. */}
      {renderTierSection('Revisit Budget', '', theme.action, revisitBudgetPins, 'watching')}
      {/* Frozen — new header (Sep 8, per Ali's report — the real fix
          for Frozen cards reading as part of Paused, with nothing
          marking where one ended and the other began). Reuses
          the exact navy + ❄ already established everywhere else in
          this app for "frozen" (the FROZEN badge, the freeze banner
          itself) rather than inventing new branding for the same
          concept on this one section header. */}
      {renderTierSection('Price Watch', '', theme.structural, frozenPins, 'freeze')}
      {/* Watching — the last one (Sep 8, per Ali's report — fair
          point: once every other category had its own header, being
          the one thing left in an unheaded flat list stopped reading
          as a deliberate simplification and just looked like an
          oversight, with the exact same bleed-into-the-section-above
          problem Frozen had a moment ago). Color reuses #7089A8
          directly — the same "Watching, with data" segment color from
          the summary bar above, so the header and the bar agree on
          what this category looks like rather than introducing a
          fourth blue for the same concept. restPins is unchanged
          (watchingWithDataPins + watchingNoResultsPins, still
          excluding needsAttentionPins) — this is a rename of the call
          site, not a new pin list. */}
      {renderTierSection('Tracking', '', '#7089A8', restPins, 'watching')}
      {/* Paused — moved to last (Sep 14, per Ali's report — real point
          raised directly: Frozen and Watching are pins someone still
          has to actively track, Paused is the one category with
          nothing currently happening, so it read as more prominent
          than it deserved sitting third. Same theme.textMuted accent
          as before — only the position changed, not the treatment. */}
      {renderPausedSection()}

      {/* Trip History entry point — a real dedicated page now, not an
          inline section (Sep 5, per Ali's report — revised the same
          day the inline version shipped: compact text rows couldn't
          show the one thing that actually mattered, the real last-found
          flight itinerary a historic pin had). Deliberately styled up,
          not a plain link — Ali's explicit ask: "fancy and colorful...
          so user would know this is history." A gradient card stands
          out against every other flat, single-color card on this page
          on purpose, the same way the History page itself uses a muted
          sepia tone against the live Dashboard's cool blue-gray — the
          entry point and the destination use opposite techniques (loud
          vs. quiet) toward the same goal: this is clearly a different
          kind of place than everything else here. */}
      {pins.length > 0 && (
        <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 20px 20px' }}>
          <div
            onClick={() => navigate('/history')}
            role="button"
            style={{
              background: theme.structural,
              borderRadius: '14px', padding: '18px 22px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px',
              flexWrap: 'wrap'
            }}
          >
            <div>
              <div style={{ fontSize: '15px', fontWeight: 800, color: theme.action, fontFamily: theme.fontDisplay }}>
                📜 See your full Trip History
              </div>
              <div style={{ fontSize: '12px', color: 'rgba(244,193,28,0.85)', marginTop: '3px' }}>
                Every trip you've ever tracked — including the exact flights we last found for them.
              </div>
            </div>
            <div style={{ background: theme.action, color: theme.structural, fontSize: '13px', fontWeight: 700, padding: '9px 18px', borderRadius: '999px', whiteSpace: 'nowrap' }}>
              View history →
            </div>
          </div>
        </div>
      )}

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px 20px 24px' }}>
        {pins.length > 0 && (
          <button
            onClick={() => navigate('/pin')}
            style={{ width: '100%', background: theme.action, color: '#1a1a1a', border: 'none', borderRadius: '14px', padding: '12px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', marginTop: '8px', fontFamily: theme.fontDisplay }}
          >
            + Pin Another Trip
          </button>
        )}
      </div>
    </div>
  );
}

export default Dashboard;